import './RichEditor.scss';

import React, { useRef, useEffect, useReducer, useState, useCallback } from 'react';
import { Spin, message, Menu, Button, Slider } from 'antd';
import { useReactiveVar } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { stateFromHTML } from 'draft-js-import-html';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import _ from 'lodash';
import getFragmentFromSelection from 'draft-js/lib/getFragmentFromSelection';
import {
  Editor,
  EditorState,
  DefaultDraftBlockRenderMap,
  convertFromRaw,
  convertToRaw,
  RichUtils,
  Modifier,
  AtomicBlockUtils,
  SelectionState,
  ContentState,
  getDefaultKeyBinding,
  ContentBlock,
  genKey,
  convertFromHTML,
  CompositeDecorator,
} from 'draft-js';

// graphql variables and cache
import { userVar, fontLoadedVar, featureFlagsVar } from 'graphql/cache';

// components
import { ToolBar, AIToolBar, FloatingMenu } from './components';
import { HyperLinkSection } from './components/HyperLinkSection/HyperLinkSection';
import Uploader from 'components/Uploader';
import FileCopier from 'components/FileCopier';
import ChevronIcon from 'components/Icons/ChevronIcon';
import RichEditorModals from './RichEditorModals/RichEditorModals';
import HyperLinkModals from './components/HyperLinkSection/HyperLinkModals';
import RenderColorPicker from './components/ColorPicker';
import Link from './components/Link/Link';

// hooks
import useSuspiciousLink from 'hooks/useSuspiciousLink';
import useWindowDimensions from 'hooks/useWindowDimensions';

// utils and helpers
import commonHelper from 'helpers';
import { resizeImage } from 'utils/image';
import helpers, { loadProposalFont, scrollToSection } from 'helpers/proposal';
import { updateSelectionInlineToolbar, getBlockSelection } from './lib/selection';
import proposalUtils from '../Draft/utils';
import {
  reducer,
  initialState,
  blockStyler,
  editorChange,
  blockRenderer,
  createCellBlock,
} from './lib';
import UploadCloudIcon from 'components/Icons/UploadCloudIcon';

import utils from './utils';
import commonUtils from 'utils/utils';

// contexts
import { useProposalContext } from 'context/proposal';

// constants
import {
  fontWeights,
  fontSizes,
  fontListEnglish,
  fontListHebrew,
} from 'pages/Proposal/constants/constants';
import { ELEMENTS_VARIABLES, testimonial } from 'constants/index';

const { Map } = Immutable;
const { SubMenu } = Menu;
let fontLoaded = [];
const isTouchOnlyDevice = commonUtils.isTouchOnlyDevice();

/**
 * RichEditor
 * @param {Object} props - The props
 * @returns {React.ReactNode} - The rich editor
 */
const RichEditor = (props) => {
  const { width } = useWindowDimensions();
  const {
    id,
    raw,
    prop,
    config,
    configText,
    element,
    setElement,
    elementValue,
    blockRenderMap,
    content,
    htmlOptions,
    simple,
    placeholder,
    contentType,
    saveProposal,
    saveUser,
    zoomValue,
    sectionName,
    setSelectedBlock,
    updateLoaderState,
    setOpenVariablesList,
    setScrollingTo,
    sectionType,
    saveProposalDraft,
    setDisablePreview,
    dropDisabled,
    setDraggingElement,
    numberOfColumns,
    columnIndex,
    typeOfColumns,
    sectionSpan,
    titleSpan,
    setElementsInSection,
    wixEditor,
    activeEditorId,
    setActiveEditorId,
    templateWixEditor,
    askAI,
    setAskAI,
    type,
    hideTools,
    fallbackStyles,
    handleToolbarElements,
  } = props;
  const user = useReactiveVar(userVar);
  const featureFlag = useReactiveVar(featureFlagsVar);

  const { checkSuspiciousUrl } = useSuspiciousLink({ proposalId: prop._id });

  const [isImageInsertionInProgress, setIsImageInsertionInProgress] = useState(false);
  const { isEditingModal, setLoading, floatingMenu, setFloatingMenu } = useProposalContext();

  const [state, dispatch] = useReducer(reducer, initialState);
  const isRtl = ['hebrew', 'arabic'].includes(prop?.language?.toLowerCase());

  /**
   * Find link entities
   * @param {Object} contentBlock - The content block
   * @param {Function} callback - The callback function
   * @param {Object} contentState - The content state
   * @returns {void}
   */
  function findLinkEntities(contentBlock, callback, contentState) {
    contentBlock.findEntityRanges((character) => {
      const entityKey = character.getEntity();
      if (entityKey) {
        const entity = contentState.getEntity(entityKey);
        const { type } = entity;
        return type === 'LINK';
      }
    }, callback);
  }

  findLinkEntities.defaultProps = {
    contentBlock: '',
    callback: () => {},
  };

  findLinkEntities.propTypes = {
    contentBlock: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
    callback: PropTypes.func,
  };

  const BRACKETS_REGEX = /{{((?:(?=([^{}]+|{{[^}]*}}))\2)*)}}/gi;

  /**
   * Find with regex
   * @param {Object} regex - The regex
   * @param {Object} contentBlock - The content block
   * @param {Function} callback - The callback function
   * @returns {void}
   */
  const findWithRegex = (regex, contentBlock, callback) => {
    const text = contentBlock.getText();
    let matchArr, start;
    // eslint-disable-next-line
    while ((matchArr = regex.exec(text)) !== null) {
      start = matchArr.index;
      callback(start, start + matchArr[0].length);
    }
  };

  /**
   * Find variable entities
   * @param {Object} contentBlock - The content block
   * @param {Function} callback - The callback function
   * @param {Object} contentState - The content state
   * @returns {void}
   */
  function findVariableEntities(contentBlock, callback, contentState) {
    findWithRegex(BRACKETS_REGEX, contentBlock, callback);
  }

  findVariableEntities.defaultProps = {
    contentBlock: '',
    callback: () => {},
  };

  findVariableEntities.propTypes = {
    contentBlock: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
    callback: PropTypes.func,
  };

  /**
   * Variable
   * @param {Object} props - The props
   * @returns {React.ReactNode} - The variable
   */
  const Variable = (props) => {
    return <span style={{ color: '#f03f3b', fontSize: '80%' }}>{props.children}</span>;
  };

  Variable.propTypes = {
    entityKey: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
    children: PropTypes.arrayOf(PropTypes.element).isRequired,
  };

  // ------------ render highligh in a span with 'highlight' class ---------------
  /**
   * Find highlight entities
   * @param {Object} contentBlock - The content block
   * @param {Function} callback - The callback function
   * @returns {void}
   */
  const findHighlightEntities = (contentBlock, callback) => {
    contentBlock.findStyleRanges((character) => {
      return character.hasStyle('HIGHLIGHT');
    }, callback);
  };

  /**
   * Highlight span
   * @param {Object} props - The props
   * @returns {React.ReactNode} - The highlight span
   */
  const HighlightSpan = (props) => {
    return <span className="highlight">{props.children}</span>;
  };
  // ------------ render highlight ---------------

  // ------------ render highligh ai in a span with 'highlight-ai' class ---------------
  /**
   * Find highlight ai entities
   * @param {Object} contentBlock - The content block
   * @param {Function} callback - The callback function
   * @returns {void}
   */
  const findHighlightAIEntities = (contentBlock, callback) => {
    contentBlock.findStyleRanges((character) => {
      return character.hasStyle('HIGHLIGHT-AI');
    }, callback);
  };

  // ------------ render highlight ai ---------------

  /**
   * Highlight and link span
   * @param {Object} props - The props
   * @returns {React.ReactNode} - The highlight and link span
   */
  const HighlightAndLinkSpan = (props) => {
    const { entityKey, contentState, isHighlight, isLink } = props;

    // If it's a link, get the entity data (URL)
    const entity = entityKey ? contentState?.getEntity(entityKey) : null;
    const linkData = entity && entity.getType() === 'LINK' ? entity.getData() : null;
    let matchVariableText, isVariable;
    // eslint-disable-next-line
    while ((matchVariableText = BRACKETS_REGEX?.exec(props?.decoratedText)) !== null) {
      isVariable = true;
    }

    // Check if it's a link, highlight, variables or all
    const isBothHighlightAndLink = isHighlight && !!linkData;
    const isBothHighlightAndVariable = isHighlight && isVariable;

    if (isBothHighlightAndLink) {
      return (
        <Link
          {...props}
          className="highlight-ai"
          dispatch={dispatch}
          promptForLink={promptForLink}
        />
      );
    } else if (isBothHighlightAndVariable) {
      return (
        <span className="highlight-ai">
          <Variable {...props} />
        </span>
      );
    } else if (isLink) {
      return <Link {...props} dispatch={dispatch} promptForLink={promptForLink} />;
    } else if (isHighlight) {
      return <span className="highlight-ai">{props.children}</span>;
    } else if (isVariable) {
      return <Variable {...props} />;
    }
  };

  /**
   * Decorator
   * @returns {Object} - The decorator
   */
  const decorator = new CompositeDecorator([
    {
      strategy: findHighlightAIEntities,
      component: (props) => <HighlightAndLinkSpan {...props} isHighlight={true} />,
    },
    {
      strategy: findLinkEntities,
      component: (props) => (
        <HighlightAndLinkSpan
          {...props}
          dispatch={dispatch}
          promptForLink={promptForLink}
          isLink={true}
        />
      ), //(props) => <Link {...props} dispatch={dispatch} promptForLink={promptForLink} />,
    },
    {
      strategy: findVariableEntities,
      component: (props) => <HighlightAndLinkSpan {...props} isVariable={true} />,
    },
    {
      strategy: findHighlightEntities,
      component: HighlightSpan,
    },
  ]);

  const editor = useRef(null);
  const imageInputRef = useRef();
  const richEditorRef = useRef();
  const editorRef = useRef();
  const displayAIToolBar = useRef();
  const displayAIToolBarCursor = useRef();
  const dragStartRef = useRef();
  const aiSaving = useRef(false);
  const undoRedoRef = useRef(false);
  const syncListMarkerStyles = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const rootNode = richEditorRef?.current;

    if (!rootNode) {
      return;
    }

    const listItems = rootNode.querySelectorAll(
      '.public-DraftEditor-content [class*="-unorderedListItem"], .public-DraftEditor-content [class*="-orderedListItem"]'
    );

    listItems.forEach((item) => {
      const textSpans = item.querySelectorAll('[data-text="true"]');

      const totalText = item.textContent?.trim() || '';

      if (textSpans.length === 0 || !totalText) {
        item.style.removeProperty('--marker-color');
        item.style.removeProperty('--marker-size');
        return;
      }

      let commonColor = null;
      let commonFontSize = null;
      let totalColorStyledTextLength = 0;
      let totalFontSizeStyledTextLength = 0;
      let allSameColor = true;
      let allSameFontSize = true;

      textSpans.forEach((span) => {
        const parentSpan = span.parentElement;
        const spanColor = parentSpan?.style?.color;
        const spanFontSize = parentSpan?.style?.fontSize;
        const spanText = span.textContent?.trim() || '';

        if (spanText.length > 0) {
          // Track color-styled text separately from font-size-styled text
          if (spanColor) {
            totalColorStyledTextLength += spanText.length;

            if (commonColor === null) {
              commonColor = spanColor;
            } else if (spanColor !== commonColor) {
              allSameColor = false;
            }
          }

          if (spanFontSize) {
            totalFontSizeStyledTextLength += spanText.length;

            if (commonFontSize === null) {
              commonFontSize = spanFontSize;
            } else if (spanFontSize !== commonFontSize) {
              allSameFontSize = false;
            }
          }
        }
      });

      const coverageThreshold = 0.95;
      const colorCoverageRatio =
        totalText.length > 0 ? totalColorStyledTextLength / totalText.length : 0;
      const fontSizeCoverageRatio =
        totalText.length > 0 ? totalFontSizeStyledTextLength / totalText.length : 0;

      if (allSameColor && commonColor && colorCoverageRatio >= coverageThreshold) {
        item.style.setProperty('--marker-color', commonColor);
      } else {
        item.style.removeProperty('--marker-color');
      }

      if (allSameFontSize && commonFontSize && fontSizeCoverageRatio >= coverageThreshold) {
        item.style.setProperty('--marker-size', commonFontSize);
      } else {
        item.style.removeProperty('--marker-size');
      }
    });
  }, [richEditorRef]);

  const [upload, uploadStatus] = Uploader();
  const [showImageProgress, toggleImageProgress] = useState(false);
  const [imageName, setImageName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({});
  const [shouldCheckUrl, setShouldCheckUrl] = useState(null);
  const [, setDeletedBlockKeys] = useState([]);
  const [bodyFont, setBodyFont] = useState(null); // store draft.bodyFont
  const [isValidUrl, setIsValidUrl] = useState(false);

  const { selectedBlock } = state;

  const [copy, copyStatus] = FileCopier();

  /**
   * Focus editor
   * @returns {void}
   */
  const focusEditor = () => {
    if (dropDisabled) {
      setReadOnly(false);
    }

    editor?.current?.focus();
    dispatch({ type: 'setEditorReadOnly', editorReadOnly: false });
  };

  useEffect(
    () => setSelectedBlock && setSelectedBlock(selectedBlock),
    [selectedBlock, setSelectedBlock]
  );

  useEffect(() => {
    // for simple section only
    if (simple === 0 && !_.isEqual(prop?.draft?.bodyFont, bodyFont)) {
      setBodyFont(prop.draft.bodyFont);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prop?.draft?.bodyFont]);

  /*
  // prevent auto focus

  useEffect(() => {
    if (bodyFont) {
      editorRef.current = state?.editorState;
      setTimeout(focusEditor, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyFont]);
  */

  const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

  useEffect(() => {
    if (shouldCheckUrl) {
      checkSuspiciousUrl(shouldCheckUrl);
      setShouldCheckUrl(null);
    }

    const currentSelection = state?.editorState ? state?.editorState?.getSelection() : null;
    const currentRaw = editorRef?.current?.getCurrentContent()
      ? convertToRaw(editorRef?.current?.getCurrentContent())
      : '';

    if (
      ((currentSelection && currentSelection?.getHasFocus() && state.keyType) ||
        !currentSelection?.getHasFocus() ||
        (currentSelection?.getHasFocus() && raw && !_.isEqual(raw, currentRaw))) &&
      !displayAIToolBar.current &&
      raw &&
      !_.isEqual(raw, currentRaw)
    ) {
      let tempEditorState = EditorState.createWithContent(
        convertFromRaw(raw, extendedBlockRenderMap),
        decorator
      );

      tempEditorState = EditorState.acceptSelection(tempEditorState, currentSelection);
      editorRef.current = tempEditorState;
      dispatch({ type: 'setEditorState', editorState: tempEditorState });
      dispatch({ type: 'setKeyType', keyType: '' });
    } else if (!editorRef.current) {
      let tempEditorState = raw
        ? EditorState.createWithContent(convertFromRaw(raw, extendedBlockRenderMap), decorator)
        : content
        ? EditorState.createWithContent(stateFromHTML(content, htmlOptions || {}), decorator)
        : EditorState.createEmpty();

      tempEditorState = EditorState.set(tempEditorState, { decorator });

      editorRef.current = tempEditorState;
      dispatch({ type: 'setEditorState', editorState: tempEditorState });
      dispatch({
        type: 'setBlockRenderMap',
        blockRenderMap: extendedBlockRenderMap,
      });
      dispatch({ type: 'setSimple', simple: simple || 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  useEffect(() => {
    setLoading({
      ...uploadStatus,
      type: uploadStatus.status === 'uploading' ? 'rich-image-upload' : false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadStatus?.status]);

  useEffect(() => {
    if (typeof window === 'undefined' || !state?.editorState) {
      return undefined;
    }

    const frameId = window.requestAnimationFrame(syncListMarkerStyles);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [
    state.editorState,
    syncListMarkerStyles,
    prop?.draft?.bodyFont,
    prop?.draft?.titleFont,
    prop?.draft?.subTitleFont,
  ]);

  /**
   * Format background selection color
   * @param {Object} editorState - The editor state
   * @param {boolean} returnEditorState - Whether to return the editor state
   * @returns {void}
   */
  const formatBackgroundSelectionColor = (editorState, returnEditorState) => {
    const selectionState = editorState.getSelection();

    if (selectionState && !selectionState?.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const blocks = contentState.getBlockMap();
      let newContentState = contentState;

      blocks.forEach((block) => {
        // remove HIGHLIGHT from the entire block
        newContentState = Modifier.removeInlineStyle(
          newContentState,
          getBlockSelection(block),
          'HIGHLIGHT-AI'
        );
      });

      try {
        // apply highlight only to the selected text
        newContentState = Modifier.applyInlineStyle(
          newContentState,
          selectionState,
          'HIGHLIGHT-AI'
        );
      } catch (e) {
        console.log(e);

        displayAIToolBar.current = '';
        displayAIToolBarCursor.current = '';
      }
      const newEditorState = EditorState.push(editorState, newContentState, 'change-inline-style');

      editorRef.current = newEditorState;
      dispatch({ type: 'setEditorState', editorState: newEditorState });

      if (returnEditorState) {
        return newEditorState;
      }
    }
  };

  /**
   * Check if the cursor is at the end of a word
   * @param {Object} editorState - The editor state
   * @param {string} blockKey - The block key
   * @returns {string} - The block key
   * @returns {void}
   */
  const isCursorAtEndOfWord = (editorState, blockKey) => {
    editorState = editorState || editorRef.current;

    const selection = editorState?.getSelection();
    const anchorKey = selection?.getAnchorKey();
    const currentContent = editorState?.getCurrentContent();
    const currentBlock = currentContent?.getBlockForKey(anchorKey);
    const blockText = currentBlock?.getText();
    const cursorPosition = selection?.getAnchorOffset();
    const focusPosition = selection?.getFocusOffset();

    if (focusPosition !== cursorPosition) {
      return;
    }

    // Check if the cursor is at the end of the block
    if (!cursorPosition && !blockText?.length) {
      return blockKey;
    }

    // Check if the cursor is at the end of the block
    if (cursorPosition === blockText?.length) {
      return cursorPosition === 0 ? '0' : cursorPosition;
    }

    if (
      typeof cursorPosition === 'number' &&
      cursorPosition === 0 &&
      (selection.getHasFocus() || displayAIToolBarCursor.current)
    ) {
      return blockKey;
    }

    // Check if the character at the cursor position is not a word character
    const charAfterCursor = blockText?.[cursorPosition];
    const isEndOfWord = /\s/.test(charAfterCursor);
    // console.log('Cursor position', cursorPosition, focusPosition, selection);

    return isEndOfWord
      ? // && selection.getHasFocus()
        cursorPosition
      : '';
  };

  /**
   * Check if the cursor is at the end of the block
   * @returns {void}
   * @returns {boolean} - Whether the cursor is at the end of the block
   */
  const isCursorAtEndOfBlock = () => {
    const editorState = editorRef.current;

    const selectionState = editorState?.getSelection();
    const contentState = editorState?.getCurrentContent();
    const blockKey = selectionState?.getAnchorKey();
    const block = contentState?.getBlockForKey(blockKey);
    const blockLength = block?.getLength();

    // Get the cursor's position in the block
    const selectionOffset = selectionState?.getAnchorOffset();

    // Check if the cursor is at the end
    return selectionOffset === blockLength;
  };

  /**
   * Use effect to add/remove AI prompt
   * @returns {void}
   */
  useEffect(() => {
    if (
      ((!user?.profile?.enableAITool && prop?.enableAITool) ||
        (user?.profile?.enableAITool && prop?.enableAITool)) &&
      editorRef.current &&
      sectionName !== 'header' &&
      sectionName !== 'signature' &&
      !state.displayLink &&
      !undoRedoRef.current
    ) {
      let selectionNew = editorRef.current?.getSelection();

      const selected = getFragmentFromSelection(editorRef?.current);

      let selectedText = selected ? selected.map((x) => x.getText()).join('\n') : '';

      if (selectedText[selectedText.length - 1] === '.') {
        selectedText = selectedText.slice(0, -1);
      }

      if (!selectedText?.trim().length && !selectionNew?.isCollapsed()) {
        const contentState = editorRef.current?.getCurrentContent();

        // Get the anchor and focus keys and offsets
        const anchorKey = selectionNew.getAnchorKey();
        const focusKey = selectionNew.getFocusKey();
        const anchorOffset = selectionNew.getAnchorOffset();
        const focusOffset = selectionNew.getFocusOffset();

        const isBackward = selectionNew.getIsBackward(); // Check if the selection is reversed
        const blockMap = contentState.getBlockMap();

        let hasStarted = false;

        blockMap.forEach((block, key) => {
          const blockText = block.getText();

          if (key === anchorKey || key === focusKey || (hasStarted && key !== focusKey)) {
            if (key === anchorKey || key === focusKey) {
              const startOffset =
                isBackward || block.getType() === 'ordered-list-item' ? focusOffset : anchorOffset;
              const endOffset =
                isBackward || block.getType() === 'ordered-list-item' ? anchorOffset : focusOffset;

              if (key === anchorKey && key === focusKey) {
                // If the selection is within the same block
                selectedText += blockText.slice(
                  Math.min(startOffset, endOffset),
                  Math.max(startOffset, endOffset)
                );
              } else if (key === anchorKey) {
                // Start block
                selectedText += blockText.slice(startOffset) + '\n';
                hasStarted = true;
              } else if (key === focusKey) {
                // End block
                selectedText += blockText.slice(0, endOffset || blockText.length);
                hasStarted = false;
              }
            } else if (hasStarted) {
              // In between blocks
              selectedText += blockText + '\n';
            }
          }
        });

        if (selectedText?.trim()?.length < 2) {
          displayAIToolBar.current = '';
          displayAIToolBarCursor.current = '';
          return null;
        }

        selectionNew = new SelectionState({
          anchorKey: selectionNew.getFocusKey(),
          anchorOffset: selectionNew.getFocusOffset(),
          focusKey: selectionNew.getAnchorKey(),
          focusOffset: selectionNew.getAnchorOffset(),
          hasFocus: true,
        });

        editorRef.current = EditorState.forceSelection(editorRef.current, selectionNew);
        dispatch({
          type: 'setEditorState',
          editorState: EditorState.forceSelection(editorRef.current, selectionNew),
        });
      }

      if (
        !selectionNew?.isCollapsed() &&
        !displayAIToolBar.current &&
        sectionName &&
        sectionName !== 'header' &&
        sectionName !== 'signature' &&
        !state.displayLinkTextInput &&
        !state.displayLinkInput &&
        !state.displayColorPicker &&
        selectedText?.trim()?.length > 1 &&
        !_.isEqual(state.aISelectionState, selectionNew)
      ) {
        dispatch({ type: 'setAISelectionState', aISelectionState: selectionNew });
        formatBackgroundSelectionColor(editorRef.current);
        displayAIToolBar.current = `${sectionName}_${simple}`;
        displayAIToolBarCursor.current = '';
        aiSaving.current = false;
      } else if (
        state.editorState &&
        selectionNew?.isCollapsed() &&
        (displayAIToolBar.current || state.displayLinkInput)
      ) {
        let newEditorState = state.editorState;

        if (state.aISelectionState) {
          newEditorState = EditorState.forceSelection(newEditorState, state.aISelectionState);

          // if (!isCursorAtEndOfBlock()) { // commented for - Double select and click on the end of the line
          newEditorState = _clearFormatting(newEditorState, false);
          // }
        }

        newEditorState = EditorState.forceSelection(newEditorState, selectionNew);
        editorRef.current = newEditorState;
        dispatch({ type: 'setDisplayColorPicker', displayColorPicker: '' });
        handleClickOutside(newEditorState);
      }

      if (selectionNew?.isCollapsed() && selectionNew.getFocusKey() && !displayAIToolBar.current) {
        const cursorPosition = isCursorAtEndOfWord(null, selectionNew.getFocusKey());

        if (
          (cursorPosition === '0' ||
            (cursorPosition && cursorPosition !== displayAIToolBarCursor.current)) &&
          !state.displayVariableInput
        ) {
          let newEditorState = editorRef.current; //state.editorState; // comment for title not loading
          if (state.aISelectionState) {
            newEditorState = EditorState.forceSelection(newEditorState, state.aISelectionState);
            newEditorState = _clearFormatting(newEditorState, false);
          }
          newEditorState = EditorState.forceSelection(newEditorState, selectionNew);
          editorRef.current = newEditorState;

          setAskAI(false);
          updateSelectionInlineToolbar(props.id, props.prop.language, dispatch);
          displayAIToolBarCursor.current = cursorPosition;
          aiSaving.current = false;
        } else if (!cursorPosition && displayAIToolBarCursor.current) {
          setAskAI(false);
          displayAIToolBarCursor.current = '';
        }
      }

      if (
        (!!selectionNew?.isCollapsed() &&
          !!selectionNew?.getHasFocus() &&
          !!askAI &&
          !!displayAIToolBarCursor.current) ||
        (!!displayAIToolBarCursor.current && selectedText && selectedText.trim().length < 2)
      ) {
        setAskAI(false);
        displayAIToolBarCursor.current = '';
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayAIToolBar.current, displayAIToolBarCursor.current, editorRef.current]);

  /**
   * Use effect to set the floating menu
   * @returns {void}
   */
  useEffect(() => {
    if (!isTouchOnlyDevice || dropDisabled) {
      return;
    }

    try {
      setFloatingMenu(null);

      let selectionNew = editorRef.current?.getSelection();
      if (!selectionNew.getHasFocus() || !selectionNew.isCollapsed()) {
        return;
      }

      // Get the DOM selection
      const selection = window.getSelection();

      if (selection.rangeCount === 0) {
        return;
      }

      const range = selection.getRangeAt(0);
      let rect = range.getBoundingClientRect();
      let node = selection.anchorNode;

      // If it's a text node, get its parent element
      if (node && node.nodeType === Node.TEXT_NODE) {
        node = node.parentElement;
      }

      // for empty node
      if (!rect || (node && rect.top === 0 && rect.bottom === 0)) {
        // Traverse up until you find a block element (like <p>, <div> with block display, or your draft.js block container)
        while (node && node !== richEditorRef.current) {
          if (node.classList) {
            // no need to show '+' for table
            if (node.classList.contains('content-editable-item')) {
              break;
            }
            // if the node have class .paragraph, then get the rect from this node
            if (node.classList.contains('paragraph')) {
              rect = node.getBoundingClientRect();
              break;
            }
          }

          node = node.parentElement;
        }
      } else {
        if (node) {
          // no need to show '+' for 'table'
          if (node.classList && node.classList.contains('content-editable-item')) {
            return;
          }
        }
      }

      if (!rect) {
        return;
      }

      const editorBounds = richEditorRef.current.getBoundingClientRect();
      // Calculate position relative to editor container
      const top = rect.top - editorBounds.top + 6;
      if (top < 0) {
        return;
      }

      setFloatingMenu({ id, top });
    } catch (err) {
      console.error('ERRxRE750', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.editorState]);

  /**
   * Handle click outside
   * @param {Object} editorState - The editor state
   * @param {string} type - The type of the click
   * @param {Object} selection - The selection
   * @returns {void}
   */
  const handleClickOutside = (editorState, type, selection) => {
    if (
      (!user?.profile?.enableAITool && prop?.enableAITool) ||
      (user?.profile?.enableAITool && prop?.enableAITool)
    ) {
      editorState = editorState || editorRef.current || state.editorState;
      if (selection) editorState = EditorState.forceSelection(editorState, selection);

      if (
        (displayAIToolBar.current || displayAIToolBarCursor.current) &&
        (editorState?.getSelection().isCollapsed() ||
          (editorState?.getSelection().isCollapsed() !== false &&
            !editorState?.getSelection()?.getHasFocus()))
      ) {
        displayAIToolBar.current = '';
        displayAIToolBarCursor.current = '';
        setAskAI(false);
        dispatch({ type: 'setAISelectionState', aISelectionState: '' });
        dispatch({ type: 'setEditorState', editorState: editorState });
        editorRef.current = editorState;
        // editor.current.blur();
      } else if (type === 'inside' && displayAIToolBar.current) {
        if (state.aISelectionState) {
          editorState = EditorState.forceSelection(editorState, state.aISelectionState);
          // if (!isCursorAtEndOfBlock()) { // commented for - Click on new empty line
          editorState = _clearFormatting(editorState, false);
          // }

          editorState = EditorState.forceSelection(editorState, editorRef?.current?.getSelection());
          dispatch({
            type: 'setAISelectionState',
            aISelectionState: editorRef?.current?.getSelection(),
          });
        }

        dispatch({ type: 'setEditorState', editorState: editorState });
        editorRef.current = editorState;
      }
    }
  };

  /**
   * Save draft
   * @returns {void}
   */
  const saveDraft = proposalUtils.updateDraftHook(saveProposalDraft, setDisablePreview)(
    sectionName,
    sectionType
  );

  /**
   * Handle editor change
   * @param {Object} props - The props
   * @returns {void}
   * @param {Object} es - The editor state
   * @param {boolean} force - Whether to force the editor change
   * @param {string} update - The type of the update
   * @param {boolean} scrollActive - Whether to scroll the editor
   * @param {Object} rest - The rest of the props
   */
  const handleEditorChange = ({ es, force = false, update, scrollActive, ...rest }) => {
    if ((update === 'table' || update === 'form') && updateLoaderState) {
      updateLoaderState(true);
    }

    editorChange({
      ...rest,
      editorState: es,
      props: { ...props, saveDraft },
      dispatch,
      editorRef,
      updateSelection: utils.updateSelection,
      force,
      editorCallback: () => {
        if ((update === 'table' || update === 'form') && updateLoaderState) {
          updateLoaderState(false);
        }
        // no need to scroll for mobile
        if (scrollActive && width > 576) {
          scrollToSection({ editorState: es });
        }
        if (displayAIToolBar.current && !state.editorState?.getSelection().isCollapsed()) {
          updateSelectionInlineToolbar(props.id, props.prop.language, dispatch);
        }
        aiSaving.current = false;
        undoRedoRef.current = '';
      },
    });
  };

  /**
   * Handle text insert
   * @param {Object} editorState - The editor state
   * @param {boolean} insertAfterBlock - Whether to insert after the block
   * @returns {void}
   */
  const handleTextInsert = async (editorState, insertAfterBlock) => {
    const contentState = editorState?.getCurrentContent();
    const contentBlock = contentState?.getBlockMap();

    if (contentBlock?.size < 2 || insertAfterBlock) {
      const newBlock = new ContentBlock({
        key: genKey(),
        type: 'unstyled',
        text: '',
        characterList: Immutable.List(),
      });
      let newBlockMap;

      if (insertAfterBlock) {
        // Split the blocks
        const blocksBefore = contentBlock.toSeq().takeUntil(function (v) {
          return v === currentBlock;
        });

        const blocksAfter = contentBlock
          .toSeq()
          .skipUntil(function (v) {
            return v === currentBlock;
          })
          .rest();

        const currentBlock = contentState.getBlockForKey(editorState.getSelection().getEndKey());

        const newBlocks = [
          [currentBlock.getKey(), currentBlock],
          [
            newBlock.key,
            new ContentBlock({
              key: newBlock.key,
              type: 'unstyled',
              text: '',
              characterList: Immutable.List(),
            }),
          ],
        ];
        newBlockMap = blocksBefore.concat(newBlocks, blocksAfter).toOrderedMap();
      } else {
        newBlockMap = contentState.getBlockMap().set(newBlock.key, newBlock);
      }

      const newEditorState = await EditorState.push(
        editorState,
        ContentState.createFromBlockArray(newBlockMap.toArray())
          .set('selectionBefore', contentState.getSelectionBefore())
          .set('selectionAfter', contentState.getSelectionAfter())
      );

      const newSelection = new SelectionState({
        anchorKey: newBlock.key,
        anchorOffset: 0,
        focusKey: newBlock.key,
        focusOffset: 0,
        hasFocus: true,
      });

      editorState = await EditorState.acceptSelection(newEditorState, newSelection);
    } else {
      editorState = await RichUtils.insertSoftNewline(
        RichUtils.insertSoftNewline(editorState || state.editorState)
      );
    }

    handleEditorChange({
      es: editorState,
      scrollActive: true,
    });
    dragStartRef.current = false;
  };

  /**
   * Toggle block type
   * @param {string} blockType - The type of the block
   * @param {boolean} customBlock - Whether to use a custom block
   * @param {Object} editorState - The editor state
   * @param {boolean} isActive - Whether the block is active
   * @returns {void}
   */
  const _toggleBlockType = async (
    blockType,
    customBlock = false,
    editorState,
    isActive = false
  ) => {
    editorState = editorState || editorRef?.current || state.editorState;

    const selectionState = state.aISelectionState || editorState.getSelection();
    let contentState = editorState.getCurrentContent();
    const startKey = selectionState?.getStartKey();
    const contentBlock = contentState?.getBlockForKey(startKey);

    const data = contentBlock?.getData();

    const needRemoval = isActive !== blockType ? true : false;

    if (customBlock) {
      if (data?.size && isActive === blockType) {
        contentState = Modifier.setBlockData(contentState, selectionState, Immutable.Map({}));
        editorState = EditorState.push(editorState, contentState, 'change-block-data'); //EditorState.createWithContent(contentState);
      } else {
        contentState = Modifier.setBlockData(
          contentState,
          selectionState,
          blockType === 'none' ? Immutable.Map({}) : Immutable.Map({ className: blockType })
        );
        editorState = EditorState.push(editorState, contentState, 'change-block-data'); //EditorState.createWithContent(contentState);
      }
      blockType = blockType === 'none' ? '' : customBlock;
    } else {
      if (data.size) {
        contentState = Modifier.setBlockData(contentState, selectionState, Immutable.Map({}));
        editorState = EditorState.push(editorState, contentState, 'change-block-data'); // EditorState.createWithContent(contentState);
      }
    }

    if (
      (blockType === 'ordered-list-item' || blockType === 'unordered-list-item') &&
      isActive !== 'unstyled' &&
      needRemoval
    ) {
      editorState = await RichUtils.toggleBlockType(editorState, isActive);
    } else if (
      (blockType === 'ordered-list-item' || blockType === 'unordered-list-item') &&
      isActive !== customBlock &&
      blockType !== isActive &&
      isActive !== 'unstyled' &&
      needRemoval
    ) {
      const { editorState: newEditorState } = utils.insertEmptyBlock('before', editorState);
      editorState = newEditorState;
    }

    handleEditorChange({
      es: await RichUtils.toggleBlockType(
        EditorState.forceSelection(editorState, selectionState),
        blockType
      ),
      force: true,
    });
  };

  /**
   * Handle image insert
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleImageInsert = async (editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, 'image');

    if (!isDropable || contentType === 'image') {
      return;
    }

    editorState = editorState || state.editorState;

    const data = {};
    // Ensure selection and startBlock exist before attempting insert
    const selection = editorState.getSelection && editorState.getSelection();
    const hasFocus = selection && selection.getHasFocus && selection.getHasFocus();
    if (!selection || !hasFocus) {
      // If selection is invalid or editor not focused, focus it at the end before inserting
      const content = editorState.getCurrentContent();
      const blockMap = content.getBlockMap();
      const lastBlock = blockMap.last();
      const key = lastBlock ? lastBlock.getKey() : undefined;
      if (key) {
        const newSelection = selection.merge({
          anchorKey: key,
          anchorOffset: lastBlock.getLength(),
          focusKey: key,
          focusOffset: lastBlock.getLength(),
          isBackward: false,
        });
        editorState = EditorState.forceSelection(editorState, newSelection);
      }
    }

    const contentStateWithEntity = editorState
      .getCurrentContent()
      .createEntity('imageUploader', 'MUTABLE', { data });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    // insertAtomicBlock needs valid selection with a block
    let es;
    try {
      es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
    } catch (e) {
      console.error('Error inserting atomic block:', e);
      return;
    }

    handleEditorChange({
      es,
      scrollActive: true,
    });
  };

  /**
   * Handle insert from media
   * @param {string} src - The source of the media
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertFromMedia = async (src, editorState) => {
    // todo:  handle other files(which are not image), progress
    if (editorState) {
      dispatch({ type: 'setDropEditorState', dropEditorState: editorState });
    }

    editorState = editorState || state.editorState;

    const url = await copy({
      fromUrl: src,
      prefix: `props/${prop._id}`,
    });

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('image', 'IMMUTABLE', {
      src: url,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    handleEditorChange({
      es: AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' '),
      scrollActive: true,
    });
  };

  /**
   * Handle insert video
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertVideo = (editorState) => {
    dispatch({ type: 'setVideoDialog', videoDialog: editorState || state.editorState });
  };

  /**
   * Handle insert gallery
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertGallery = async (editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, 'gallery');
    if (!isDropable) {
      return;
    }
    editorState = editorState || state.editorState;

    const data = [];
    const contentState = editorState.getCurrentContent();

    const contentStateWithEntity = contentState.createEntity('gallery', 'MUTABLE', {
      data,
      config: {},
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
    handleEditorChange({
      es,
      scrollActive: true,
    });
  };

  /**
   * Handle insert table
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertTable = async (editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, 'table');
    if (!isDropable) {
      return;
    }

    let newEditorState = editorState || state.editorState;

    const data = [];
    const row = 3;
    const column = 3;

    for (let r = 0; r < row; r++) {
      data[r] = {};
      data[r].id = r + 1;
      for (let c = 0; c < column; c++) {
        data[r][c + 1] = '-';
      }
    }

    const contentState = newEditorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('table', 'MUTABLE', {
      data,
      config: { topRowColor: 'lightgrey', rowColor: 'white' },
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const es = await AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');

    handleEditorChange({
      es,
      update: 'table',
      scrollActive: true,
    });

    dispatch({ type: 'setDropEditorState', dropEditorState: '' });
  };

  /**
   * Handle insert testimonial
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertTestimonial = async (editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, 'testimonial');
    if (!isDropable) {
      return;
    }
    let newEditorState = editorState || state.editorState;

    const contentState = newEditorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('testimonial', 'MUTABLE', {
      data: [testimonial.data, testimonial.data, testimonial.data],
      config: {},
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const es = await AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');

    handleEditorChange({
      es,
      update: 'testimonial',
      scrollActive: true,
    });

    dispatch({ type: 'setDropEditorState', dropEditorState: '' });
  };

  /**
   * Handle insert form
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertForm = async (editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, 'form');
    if (!isDropable) {
      return;
    }
    editorState = editorState || state.editorState;

    // Add this to automatically open the edit modal
    const formEditObject = {
      form: [],
      config: { editable: false, columns: 1 }, // Special value for new form blocks
      save: async (nv, saveSuccess) => {
        const data = [...nv.form];
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('form', 'MUTABLE', {
          data: data,
          config: nv.config,
          language: prop?.language?.toLowerCase(),
        });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
        dispatch({ type: 'setLiveTeXEdits', liveTeXEdits: Map() });
        dispatch({ type: 'setEditorState', editorState: es });
        editorRef.current = editorState;
        saveSuccess();
        handleEditorChange({
          es,
          update: 'form',
          scrollActive: true,
        });
      },
      cancel: () => {
        // Cancel logic here
      },
      editorState,
    };
    dispatch({ type: 'setFormEdit', formEdit: formEditObject });

    setTimeout(() => focusEditor(), 0);
  };

  /**
   * Handle insert price miles
   * @param {string} type - The type of the price miles
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertPriceMiles = async (type, editorState) => {
    const isDropable = commonUtils.calculatePricingDropable(titleSpan, sectionSpan, type);
    if (!isDropable) {
      return;
    }
    const es = await utils.insertTeXBlock(editorState || state.editorState, type);

    dispatch({ type: 'setLiveTeXEdits', liveTeXEdits: Map() });
    dispatch({ type: 'setEditorState', editorState: es });
    editorRef.current = es;
    setTimeout(() => focusEditor(), 0);
  };

  /**
   * Handle insert divider
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertDivider = async (editorState) => {
    editorState = editorState || state.editorState;

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('divider', 'IMMUTABLE', {});
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });

    const es = await AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    handleEditorChange({
      es,
      scrollActive: true,
    });
  };

  /**
   * Handle insert HTML
   * @param {Object} editorState - The editor state
   * @returns {void}
   */
  const handleInsertHTML = (editorState) => {
    dispatch({
      type: 'setHtmlEdit',
      htmlEdit: { htmlCode: '', editorState: editorState || state.editorState },
    });
  };

  /**
   * Insert image
   * @param {Object} event - The event
   * @returns {void}
   */
  const insertImage = async (event) => {
    try {
      event.preventDefault();
      const { files } = event.target;
      if (files && !files.length) return;
      const [file] = files;
      // Only allow image files
      if (file && !file.type.startsWith('image/')) {
        message.error('Only image is allowed.');
        return;
      }

      const resizedImage = await resizeImage({
        file,
        path: 'image',
        maxWidth: 950,
      });

      const { url } = await upload(resizedImage, `props/${prop._id}/section`);

      const img = new Image();
      img.src = url;
      img.onload = async () => {
        try {
          const { width, height } = img;
          const config = {
            size: {
              width,
              height,
              ratio: parseFloat((width / height).toFixed(2)),
            },
            imageType: file.type.substring(6), // add image type
          };

          const editorState = state.dropEditorState || state.editorState;
          const contentState = editorState.getCurrentContent();
          const contentStateWithEntity = contentState.createEntity('image', 'IMMUTABLE', {
            src: url,
            config,
          });
          const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
          const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');

          handleEditorChange({
            es,
            scrollActive: true,
          });
          dispatch({ type: 'setDropEditorState', dropEditorState: '' });
          event.target.value = null;
        } catch (error) {
          console.log(error.message || 'Failed to insert image.');
        }
      };
    } catch (error) {
      message.error(error.message);
    }
  };

  /**
   * Handle drop image
   * @param {Object} files - The files
   * @param {string} blockKey - The block key
   * @returns {void}
   */
  const handleDropImage = async (files, blockKey = null) => {
    try {
      const [file] = files;
      if (!file?.type) {
        throw new Error('Please select an image');
      }
      if (!file.type.startsWith('image/')) {
        throw new Error('Only image is allowed.');
      }
      setImageName(file.name);
      toggleImageProgress(true);

      await resizeImage({ file, maxWidth: 1000, path: 'sections' });
      const { url } = await upload(file, `props/${prop._id}/section`);
      const { editorState } = state;

      const img = new Image();
      img.src = url;
      img.onload = async () => {
        const { width, height } = img;
        const config = {
          size: {
            // width, // width will be calculated on first image load
            // height,
            ratio: parseFloat((width / height).toFixed(2)),
          },
          imageType: file.type.substring(6),
        };

        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('image', 'IMMUTABLE', {
          src: url,
          config,
        });

        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        if (blockKey) {
          const blockMap = contentState.getBlockMap();
          const block = blockMap.get(blockKey);
          if (block) {
            const blockType = block.getType();
            if (blockType === 'atomic') {
              const existingEntityKey = block?.getEntityAt(0);
              if (existingEntityKey) {
                const existingEntity = contentState.getEntity(existingEntityKey);
                if (existingEntity && existingEntity.getType() === 'imageUploader') {
                  const selection = SelectionState.createEmpty(blockKey).merge({
                    anchorOffset: 0,
                    focusOffset: block.getLength(),
                  });

                  const contentStateWithoutBlock = Modifier.removeRange(
                    contentState,
                    selection,
                    'forward'
                  );

                  const editorStateWithoutBlock = EditorState.push(
                    editorState,
                    contentStateWithoutBlock,
                    'remove-range'
                  );

                  const newEditorState = AtomicBlockUtils.insertAtomicBlock(
                    editorStateWithoutBlock,
                    entityKey,
                    ' '
                  );

                  handleEditorChange({
                    es: newEditorState,
                    scrollActive: true,
                  });
                  return;
                }
              }
            }

            try {
              const existingEntityKey = block?.getEntityAt(0);
              if (existingEntityKey) {
                const updatedContentState = contentState.mergeEntityData(existingEntityKey, {
                  src: url,
                  config,
                });

                const newEditorState = EditorState.push(
                  editorState,
                  updatedContentState,
                  'apply-entity'
                );

                dispatch({ type: 'setEditorState', editorState: newEditorState });

                if (editorRef.current) {
                  editorRef.current = newEditorState;
                }

                const forceUpdateEditorState = EditorState.forceSelection(
                  newEditorState,
                  newEditorState.getSelection()
                );

                dispatch({ type: 'setEditorState', editorState: forceUpdateEditorState });

                setTimeout(() => {
                  dispatch({ type: 'setEditorState', editorState: forceUpdateEditorState });
                }, 0);

                handleEditorChange({
                  es: forceUpdateEditorState,
                  scrollActive: true,
                });
              } else {
                const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
                handleEditorChange({
                  es,
                  scrollActive: true,
                });
              }
            } catch (error) {
              console.error('Error updating image entity:', error);
              const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
              handleEditorChange({
                es,
                scrollActive: true,
              });
            }
          } else {
            console.warn(`Block with key ${blockKey} not found, inserting new block`);
            const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
            handleEditorChange({
              es,
              scrollActive: true,
            });
          }
        } else {
          const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
          handleEditorChange({
            es,
            scrollActive: true,
          });
        }
      };
    } catch (error) {
      message.error(error.message);
    }
    toggleImageProgress(false);
  };

  /**
   * Handle image drop
   * @param {Object} e - The event
   * @returns {void}
   */
  const onImageDrop = (e) => {
    e.preventDefault();
    handleDropImage(e.dataTransfer.files);
  };

  /**
   * Handle image change
   * @param {Object} ev - The event
   * @returns {void}
   */
  const handleImageChange = (ev) => {
    handleDropImage(ev.target.files);
  };

  /**
   * Handle image select URL
   * @param {string} imageUrl - The image URL
   * @param {string} blockKey - The block key
   * @returns {void}
   */
  const handleImageSelectUrl = async (imageUrl, blockKey = null) => {
    if (isImageInsertionInProgress) return;

    setIsImageInsertionInProgress(true);

    const MSG_KEY = 'image-insert';
    message.loading({ content: 'Inserting image...', key: MSG_KEY, duration: 0 });

    const finish = (successMsg) => {
      setIsImageInsertionInProgress(false);
      toggleImageProgress(false);
      if (successMsg) {
        message.success({ content: successMsg, key: MSG_KEY });
      } else {
        message.destroy(MSG_KEY);
      }
    };

    try {
      if (imageUrl && !imageUrl.startsWith('http')) {
        throw new Error("Something isn't right with the image URL.");
      }

      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const file = new File([blob], 'image.png', { type: blob.type });

      setImageName(file.name);
      toggleImageProgress(true);

      const { width, height } = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () =>
          reject(new Error('Failed to load the selected image. Please try again.'));
        img.src = imageUrl;
      });

      const config = {
        size: { ratio: parseFloat((width / height).toFixed(2)) },
        imageType: file.type.substring(6),
      };

      const { editorState } = state;
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('image', 'IMMUTABLE', {
        src: imageUrl,
        config,
      });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      const insertNewBlock = () => {
        const es = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');
        handleEditorChange({ es, scrollActive: true });
      };

      const SUCCESS_MSG = 'Image inserted successfully';

      if (!blockKey) {
        insertNewBlock();
        finish(SUCCESS_MSG);
        return;
      }

      const block = contentState.getBlockMap().get(blockKey);

      if (!block) {
        insertNewBlock();
        finish(SUCCESS_MSG);
        return;
      }

      if (block.getType() === 'atomic') {
        const existingEntityKey = block.getEntityAt(0);
        const existingEntity = existingEntityKey ? contentState.getEntity(existingEntityKey) : null;

        if (existingEntity?.getType() === 'imageUploader') {
          const selection = SelectionState.createEmpty(blockKey).merge({
            anchorOffset: 0,
            focusOffset: block.getLength(),
          });
          const contentStateWithoutBlock = Modifier.removeRange(contentState, selection, 'forward');
          const editorStateWithoutBlock = EditorState.push(
            editorState,
            contentStateWithoutBlock,
            'remove-range'
          );
          const newEditorState = AtomicBlockUtils.insertAtomicBlock(
            editorStateWithoutBlock,
            entityKey,
            ' '
          );
          handleEditorChange({ es: newEditorState, scrollActive: true });
        } else if (existingEntityKey) {
          const updatedContentState = contentState.mergeEntityData(existingEntityKey, {
            src: imageUrl,
            config,
          });
          const newEditorState = EditorState.push(editorState, updatedContentState, 'apply-entity');

          if (editorRef.current) editorRef.current = newEditorState;

          const forceUpdatedState = EditorState.forceSelection(
            newEditorState,
            newEditorState.getSelection()
          );
          dispatch({ type: 'setEditorState', editorState: forceUpdatedState });
          setTimeout(() => dispatch({ type: 'setEditorState', editorState: forceUpdatedState }), 0);
          handleEditorChange({ es: forceUpdatedState, scrollActive: true });
        }

        finish(SUCCESS_MSG);
        return;
      }

      // Non-atomic block: update existing entity data or fall back to inserting a new block
      try {
        const existingEntityKey = block.getEntityAt(0);
        if (existingEntityKey) {
          const updatedContentState = contentState.mergeEntityData(existingEntityKey, {
            src: imageUrl,
            config,
          });
          const newEditorState = EditorState.push(editorState, updatedContentState, 'apply-entity');

          if (editorRef.current) editorRef.current = newEditorState;

          const forceUpdatedState = EditorState.forceSelection(
            newEditorState,
            newEditorState.getSelection()
          );
          dispatch({ type: 'setEditorState', editorState: forceUpdatedState });
          setTimeout(() => dispatch({ type: 'setEditorState', editorState: forceUpdatedState }), 0);
          handleEditorChange({ es: forceUpdatedState, scrollActive: true });
        } else {
          insertNewBlock();
        }
      } catch {
        insertNewBlock();
      }

      finish(SUCCESS_MSG);
    } catch (error) {
      message.error({ content: error.message, key: MSG_KEY });
      setIsImageInsertionInProgress(false);
      toggleImageProgress(false);
    }
  };

  /**
   * Handle image drag over
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  /**
   * Handle direct image insert
   * @param {string} src - The source of the image
   * @returns {Promise} - Promise that resolves when the image is inserted
   */
  const handleDirectImageInsert = async (src) => {
    const url = await copy({
      fromUrl: src,
      prefix: `props/${prop._id}`,
    });
    const { editorState } = state;

    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('image', 'IMMUTABLE', {
      src: url,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const es = await AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ');

    handleEditorChange({
      es,
      scrollActive: true,
    });
  };

  /**
   * Handle the element insertion
   * @returns {void}
   */
  useEffect(() => {
    if (selectedBlock && element) {
      if (element === 'text') handleTextInsert();
      else if (element === 'blockquote') _toggleBlockType(element);
      else if (element === 'image') handleImageInsert();
      else if (element === 'video') handleInsertVideo();
      else if (element === 'gallery') handleInsertGallery();
      else if (element === 'table') handleInsertTable();
      else if (element === 'testimonial') handleInsertTestimonial();
      else if (element === 'form') handleInsertForm();
      else if (element === 'price') handleInsertPriceMiles('price', editorRef?.current);
      else if (element === 'miles') handleInsertPriceMiles('miles');
      else if (element === 'divider') handleInsertDivider();
      else if (element === 'add-media-image') handleDirectImageInsert(elementValue);
      else if (element === 'html') handleInsertHTML();
      setElement('');
    } else if (element) {
      setElement('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, elementValue]);

  /**
   * Prompt for variable
   * @param {Object} editorState - The editor state
   * @param {boolean} onlyOpenToolBar - Whether to only open the toolbar
   * @param {string} variableName - The name of the variable
   * @returns {void}
   */
  const promptForVariable = (editorState, onlyOpenToolBar, variableName) => {
    variableName = variableName.replace('{{', '');
    variableName = variableName.replace('}}', '');
    const [category] = variableName.split('.');

    if (onlyOpenToolBar) {
      setScrollingTo(category);
    }

    setScrollingTo(category);

    if (!onlyOpenToolBar) {
      const selection = editorState.getSelection();
      dispatch({ type: 'setSelectionState', selectionState: selection });
      dispatch({ type: 'setDisplayVariableInput', displayVariableInput: true });
    }

    setOpenVariablesList(true);
  };

  /**
   * Prompt for link
   * @param {string} type - The type of the link
   * @param {string} text - The text of the link
   * @param {string} url - The URL of the link
   * @returns {void}
   */
  const promptForLink = (type = 'fresh-link', text, url) => {
    const { editorState } = state;
    const selection = editorState.getSelection();
    displayAIToolBar.current = '';
    displayAIToolBarCursor.current = '';
    if (type === 'fresh-link' && selection.isCollapsed()) {
      return;
    }
    const isUpdate = type === 'update-link-text';
    dispatch({ type: 'setDisplayLink', displayLink: type });
    dispatch({ type: 'setLinkTextValue', linkTextValue: isUpdate ? text : '' });
    dispatch({ type: 'setLinkUrlValue', linkUrlValue: isUpdate ? url : '' });
    dispatch({ type: 'setSelectionState', selectionState: selection });
    setIsValidUrl(isUpdate);
  };
  // Link ends here

  /**
   * Prompt color box
   * @param {string} type - The type of the color
   * @returns {void}
   */
  const promptColorBox = (type = 'COLOR') => {
    // type = COLOR or BACKGROUND
    const editorState = editorRef.current || state.editorState;
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      dispatch({ type: 'setSelectionState', selectionState: selection });
      dispatch({ type: 'setDisplayColorPicker', displayColorPicker: type });
      dispatch({ type: 'setEditorReadOnly', editorReadOnly: true });
    }
  };

  /**
   * Close color
   * @returns {void}
   */
  const closeColor = () => {
    if (!state?.displayColorPicker) {
      return;
    }
    dispatch({ type: 'setDisplayColorPicker', displayColorPicker: false });
    dispatch({ type: 'setEditorReadOnly', editorReadOnly: false });
  };
  // Color ends here

  /**
   * Toggle custom block style
   * @param {Object} blockType - The block type
   * @param {string} type - The type of the block
   * @returns {void}
   */
  const _toggleCustomBockStyle = ({ blockType, type }) => {
    let { editorState } = state;
    let selection = editorState.getSelection();
    let startKey = selection.getStartKey();
    let endKey = selection.getEndKey();
    let currentContent = editorState.getCurrentContent();
    let prevBlockTypes = RichUtils.getCurrentBlockType(editorState);
    let newBlockType = '';
    let blocksToUpdate = [];
    let blockKey = startKey;
    while (blockKey !== endKey) {
      blocksToUpdate.push(blockKey);
      blockKey = currentContent.getKeyAfter(blockKey);
    }
    blocksToUpdate.push(endKey);

    prevBlockTypes = prevBlockTypes.split(' ');

    let directionBlockType = prevBlockTypes.find((prevBlockType) =>
      prevBlockType.match(/direction/)
    );
    let alignBlockType = prevBlockTypes.find((prevBlockType) => prevBlockType.match(/align/));
    let heightBlockType = prevBlockTypes.find((prevBlockType) => prevBlockType.match(/height/));
    let intentBlockType = prevBlockTypes.find((prevBlockType) => prevBlockType.match(/intent/));
    let listBlockType = prevBlockTypes.find((prevBlockType) => prevBlockType.match(/list-item/));

    let newContentState = currentContent;
    // Update className for each block
    if (type === 'align') {
      if (blockType === alignBlockType) alignBlockType = '';
      else if (blockType !== 'unstyled') alignBlockType = blockType;
      else alignBlockType = '';
    } else if (type === 'direction') {
      if (directionBlockType && blockType !== 'unstyled') {
        if (directionBlockType.match(/ltr/)) {
          directionBlockType = 'editor-direction-rtl';
        } else {
          directionBlockType = 'editor-direction-ltr';
        }
      } else if (blockType !== 'unstyled') directionBlockType = 'editor-direction-rtl';
      else directionBlockType = '';
    } else if (type === 'height') {
      if (listBlockType) {
        blocksToUpdate.forEach((blockKey) => {
          let block = newContentState.getBlockForKey(blockKey);
          let blockData = block.getData();
          prevBlockTypes = prevBlockTypes.filter(
            (className) => !className.startsWith('line-height__')
          );
          if (blockType !== 'unstyled') {
            prevBlockTypes.push(blockType);
          }

          // Merge updated data into the block
          let newData = blockData.set('className', prevBlockTypes.join(' '));
          let newBlock = block.merge({ data: newData });

          // Update the content state with the modified block
          newContentState = newContentState.merge({
            blockMap: newContentState.getBlockMap().set(blockKey, newBlock),
          });
        });
      } else if (blockType === heightBlockType) heightBlockType = '';
      else if (blockType !== 'unstyled') heightBlockType = blockType;
      else heightBlockType = '';
    } else if (type === 'intent') {
      let intentCount = intentBlockType?.split('-')[2] || 0;
      if (intentCount < 26 && intentCount >= 0) {
        if (blockType.match(/left/)) isRtl ? ++intentCount : --intentCount;
        else if (blockType.match(/right/)) isRtl ? --intentCount : ++intentCount;
      }

      intentBlockType = `intent-${isRtl ? 'right' : 'left'}-${intentCount}`;
    }

    newBlockType = `${alignBlockType || ''}  ${directionBlockType || ''} ${heightBlockType || ''} ${
      intentBlockType || ''
    } ${listBlockType || ''}`;

    // Update the editor state with the modified content
    const isLineHeight = type === 'height' && listBlockType;

    handleEditorChange({
      es: isLineHeight
        ? EditorState.push(editorState, newContentState, newBlockType)
        : RichUtils.toggleBlockType(editorState, newBlockType),
    });
  };

  /**
   * Toggle inline style
   * @param {Object} inlineStyle - The inline style
   * @param {Object} es - The editor state
   * @param {boolean} withoutText - Whether to without text
   * @returns {void}
   */
  const _toggleInlineStyle = ({ inlineStyle, es, withoutText }) => {
    if (inlineStyle === 'LINK') {
      if (withoutText) promptForLink('fresh-link-text');
      else promptForLink('fresh-link');
    } else if (inlineStyle === 'COLOR' || inlineStyle === 'BACKGROUND') {
      promptColorBox(inlineStyle);
      displayAIToolBar.current = '';
      displayAIToolBarCursor.current = '';
    } else if (inlineStyle === 'right-intent' || inlineStyle === 'left-intent') {
      _toggleCustomBockStyle({
        blockType: inlineStyle,
        type: 'intent',
      });
    } else {
      if (inlineStyle.includes('rgba') && type !== 'header') {
        debounceColorChange(es, inlineStyle);
      } else {
        handleEditorChange({
          es: RichUtils.toggleInlineStyle(es || state.editorState, inlineStyle),
        });
      }
    }
  };

  /**
   * Debounce color change
   * @param {Object} es - The editor state
   * @param {string} inlineStyle - The inline style
   * @returns {void}
   */
  const debounceColorChange = useRef(
    _.debounce((es, inlineStyle) => {
      handleEditorChange({ es: RichUtils.toggleInlineStyle(es || state.editorState, inlineStyle) });
    }, 500)
  ).current;

  /**
   * Toggle font inline style
   * @param {Object} inlineStyle - The inline style
   * @param {string} type - The type of the font
   * @param {Array} fontFamilies - The font families
   * @param {string} weight - The weight of the font
   * @returns {void}
   */
  const _toggleFontInlineStyle = ({ inlineStyle, type, fontFamilies, weight }) => {
    const editorState = editorRef.current || state.editorState;
    const selection = editorState.getSelection();

    let nextContentState = '';

    if (type === 'family') {
      // Let's just allow one font at a time. Turn off all active fonts.
      fontFamilies = fontFamilies.map((fontList) => fontList.family);
      nextContentState = fontFamilies.reduce((contentState, font) => {
        return Modifier.removeInlineStyle(contentState, selection, font);
      }, editorState.getCurrentContent());

      // if weight is also toggled
      if (weight) {
        nextContentState = fontWeights.reduce((contentState, weight) => {
          return Modifier.removeInlineStyle(contentState, selection, `${weight}`);
        }, nextContentState);
      }
    } else if (type === 'weight') {
      // Let's just allow one weight at a time. Turn off all active weight.
      nextContentState = fontWeights.reduce((contentState, weight) => {
        return Modifier.removeInlineStyle(contentState, selection, `${weight}`);
      }, editorState.getCurrentContent());
    } else if (type === 'size' || type === 'increase-size' || type === 'decrease-size') {
      // Let's just allow one size at a time. Turn off all active size.
      nextContentState = fontSizes.reduce((contentState, size) => {
        return Modifier.removeInlineStyle(contentState, selection, size);
      }, editorState.getCurrentContent());
    }

    if (type === 'increase-size' || type === 'decrease-size') {
      const _currentStyle = editorState.getCurrentInlineStyle();

      let fontSizeIndex = fontSizes.indexOf('16px');

      // eslint-disable-next-line no-unused-vars
      _currentStyle.find((style) => {
        const index = fontSizes.indexOf(style);
        if (index > -1) {
          fontSizeIndex = index;
          return true;
        }
        return false;
      });

      if (type === 'increase-size') {
        fontSizeIndex = Math.min(fontSizeIndex + 1, fontSizes.length - 1);
      } else if (type === 'decrease-size') {
        fontSizeIndex = Math.max(fontSizeIndex - 1, 0);
      }
      inlineStyle = fontSizes[fontSizeIndex];
    }

    let nextEditorState = EditorState.push(editorState, nextContentState, 'change-inline-style');
    const currentStyle = editorState.getCurrentInlineStyle();

    // Unset style override for current style.
    if (selection.isCollapsed()) {
      nextEditorState = currentStyle.reduce((currentState, font) => {
        return RichUtils.toggleInlineStyle(currentState, font);
      }, nextEditorState);
    }

    // If the style is being toggled on, apply it.
    if (!currentStyle.has(inlineStyle)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, `${inlineStyle}`);
    }

    // if weight is also toggled (along with family or size)
    if (weight && !currentStyle.has(weight)) {
      nextEditorState = RichUtils.toggleInlineStyle(nextEditorState, `${weight}`);
    }

    handleEditorChange({ es: nextEditorState });
  };

  /**
   * Clear formatting
   * @param {Object} es - The editor state
   * @param {string} clearBlockType - The type of the clear block
   * @returns {void}
   */
  const _clearFormatting = (es, clearBlockType) => {
    const editorState = es || editorRef.current || state.editorState;

    const selectionState = editorState.getSelection();
    const anchorKey = selectionState.getAnchorKey();
    const focusKey = selectionState.getFocusKey();
    const currentContent = editorState.getCurrentContent();
    const currentBlock = currentContent.getBlockForKey(anchorKey);
    const selectionStart = selectionState.getStartOffset();
    const selectionEnd = selectionState.getEndOffset();

    if (anchorKey === focusKey) {
      let newEditorState = editorState;
      if (clearBlockType) {
        const selectedText = currentBlock.getText().slice(selectionStart, selectionEnd);
        const contentWithoutStyles = Modifier.replaceText(
          editorState.getCurrentContent(),
          selectionState,
          selectedText,
          null
        );

        newEditorState = EditorState.push(editorState, contentWithoutStyles, 'change-inline-style');
        newEditorState = RichUtils.toggleBlockType(newEditorState, '');
      } else {
        if (dragStartRef.current) {
          dragStartRef.current = '';
          // doesnot removed selection when drag and drop text starts
          return editorState;
        }
        let selectionState = editorState.getSelection();

        if ((selectionState && !selectionState.isCollapsed()) || state.aISelectionState) {
          const contentState = editorState.getCurrentContent();
          const blocks = contentState.getBlockMap();
          let newContentState = contentState;

          blocks.forEach((block) => {
            // remove HIGHLIGHT from the entire block
            newContentState = Modifier.removeInlineStyle(
              newContentState,
              getBlockSelection(block),
              'HIGHLIGHT-AI'
            );
          });

          newEditorState = EditorState.push(editorState, newContentState, 'change-inline-style');
        }
      }

      if (es) {
        return newEditorState;
      }

      handleEditorChange({ es: newEditorState });
    } else {
      let headerRaw = '';
      let rawBlocks = [];
      if (sectionName === 'header') {
        let shouldBreak = false;
        for (const key of Object.keys(prop?.draft[sectionName])) {
          if (key.indexOf('raw') > -1) {
            for (const block of prop?.draft[sectionName][key].blocks) {
              if (block.key === focusKey || block.key === anchorKey) {
                shouldBreak = true;
                rawBlocks = JSON.parse(JSON.stringify(prop?.draft[sectionName][key].blocks));
                headerRaw = key;
                break;
              }
            }
          }
          if (shouldBreak) {
            break;
          }
        }
      } else {
        rawBlocks = JSON.parse(JSON.stringify(prop?.draft[sectionName]?.raw?.blocks));
      }
      let shouldSelect = false;

      if (selectionState.getIsBackward()) {
        for (let i = 0; i < rawBlocks.length; i++) {
          if (focusKey === rawBlocks[i].key) {
            shouldSelect = true;
            const inlineStyleRanges = [];
            for (let j = 0; j < rawBlocks[i].inlineStyleRanges.length; j++) {
              if (rawBlocks[i].inlineStyleRanges[j].offset < selectionStart) {
                if (
                  rawBlocks[i].inlineStyleRanges[j].offset +
                    rawBlocks[i].inlineStyleRanges[j].length >
                  selectionStart
                ) {
                  inlineStyleRanges.push({
                    ...rawBlocks[i].inlineStyleRanges[j],
                    length:
                      rawBlocks[i].inlineStyleRanges[j].offset +
                      rawBlocks[i].inlineStyleRanges[j].length -
                      selectionStart,
                  });
                } else {
                  inlineStyleRanges.push(rawBlocks[i].inlineStyleRanges[j]);
                }
              }
            }
            rawBlocks[i].inlineStyleRanges = inlineStyleRanges;
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          } else if (rawBlocks[i].key === anchorKey) {
            shouldSelect = false;
            const inlineStyleRanges = [];
            for (let j = 0; j < rawBlocks[i].inlineStyleRanges.length; j++) {
              if (rawBlocks[i].inlineStyleRanges[j].offset > selectionEnd) {
                inlineStyleRanges.push(rawBlocks[i].inlineStyleRanges[j]);
              } else if (
                rawBlocks[i].inlineStyleRanges[j].offset +
                  rawBlocks[i].inlineStyleRanges[j].length >
                selectionEnd
              ) {
                inlineStyleRanges.push({
                  ...rawBlocks[i].inlineStyleRanges[j],
                  offset: selectionEnd,
                });
              }
            }
            rawBlocks[i].inlineStyleRanges = inlineStyleRanges;
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          } else if (shouldSelect) {
            rawBlocks[i].inlineStyleRanges = [];
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          }
        }
      } else {
        for (let i = 0; i < rawBlocks.length; i++) {
          if (anchorKey === rawBlocks[i].key) {
            shouldSelect = true;
            const inlineStyleRanges = [];
            for (let j = 0; j < rawBlocks[i].inlineStyleRanges.length; j++) {
              if (rawBlocks[i].inlineStyleRanges[j].offset < selectionStart) {
                if (
                  rawBlocks[i].inlineStyleRanges[j].offset +
                    rawBlocks[i].inlineStyleRanges[j].length >
                  selectionStart
                ) {
                  inlineStyleRanges.push({
                    ...rawBlocks[i].inlineStyleRanges[j],
                    length:
                      rawBlocks[i].inlineStyleRanges[j].offset +
                      rawBlocks[i].inlineStyleRanges[j].length -
                      selectionStart,
                  });
                } else {
                  inlineStyleRanges.push(rawBlocks[i].inlineStyleRanges[j]);
                }
              }
            }
            rawBlocks[i].inlineStyleRanges = inlineStyleRanges;
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          } else if (rawBlocks[i].key === focusKey) {
            shouldSelect = false;
            const inlineStyleRanges = [];
            for (let j = 0; j < rawBlocks[i].inlineStyleRanges.length; j++) {
              if (rawBlocks[i].inlineStyleRanges[j].offset > selectionEnd) {
                inlineStyleRanges.push(rawBlocks[i].inlineStyleRanges[j]);
              } else if (
                rawBlocks[i].inlineStyleRanges[j].offset +
                  rawBlocks[i].inlineStyleRanges[j].length >
                selectionEnd
              ) {
                inlineStyleRanges.push({
                  ...rawBlocks[i].inlineStyleRanges[j],
                  offset: selectionEnd,
                });
              }
            }
            rawBlocks[i].inlineStyleRanges = inlineStyleRanges;
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          } else if (shouldSelect) {
            rawBlocks[i].inlineStyleRanges = [];
            if (rawBlocks[i].type !== 'atomic') {
              rawBlocks[i].type = 'unstyled';
            }
          }
        }
      }

      let newEditorState = editorState;

      if (clearBlockType) {
        newEditorState = EditorState.createWithContent(
          convertFromRaw({
            blocks: rawBlocks,
            entityMap:
              sectionName === 'header'
                ? prop?.draft[sectionName][headerRaw]?.entityMap
                : prop?.draft[sectionName]?.raw?.entityMap,
          }),
          decorator
        );
        newEditorState = RichUtils.toggleBlockType(newEditorState, '');
      } else {
        const selectionState = editorState.getSelection();

        if ((selectionState && !selectionState.isCollapsed()) || state.aISelectionState) {
          const contentState = editorState.getCurrentContent();
          const blocks = contentState.getBlockMap();
          let newContentState = contentState;

          blocks.forEach((block) => {
            // remove HIGHLIGHT from the entire block
            newContentState = Modifier.removeInlineStyle(
              newContentState,
              getBlockSelection(block),
              'HIGHLIGHT-AI'
            );
          });

          newEditorState = EditorState.push(editorState, newContentState, 'change-inline-style');
        }
      }

      if (es) {
        return newEditorState;
      }

      handleEditorChange({ es: newEditorState });
    }
  };

  /**
   * On split section
   * @returns {void}
   */
  const _onSplitSection = async () => {
    updateLoaderState(true);
    const { editorState } = state;
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    let splitedContentState = Modifier.splitBlock(contentState, selectionState);
    let selectionAfter = splitedContentState.getSelectionAfter();

    let startBlock = selectionAfter.getStartKey();

    let blockAsArray = splitedContentState.getBlocksAsArray();

    let newBlock = [];
    let oldBlock = [];

    let ifReached = false;

    blockAsArray.forEach((block) => {
      if (ifReached || block.key === startBlock) {
        newBlock.push(block);
        ifReached = true;
      } else {
        oldBlock.push(block);
      }
    });

    let newContentState = ContentState.createFromBlockArray(newBlock);
    let oldContentState = ContentState.createFromBlockArray(oldBlock);

    const sectionId = uuidv4();

    const sectionOrder = helpers.findSectionOrder({
      proposal: prop,
      sectionId,
      sectionName,
      position: helpers.findNewSectionPosition({
        wrapperType: 'normal',
        sectionAlignment: 'below',
      }),
    });

    await saveProposal(
      {
        'draft.sectionorder': sectionOrder,
        [`draft.${sectionId}.raw`]: convertToRaw(newContentState),
        [`draft.${sectionName}.raw`]: convertToRaw(oldContentState),
      },
      () => {
        const newEditorState = EditorState.createWithContent(oldContentState);
        handleEditorChange({ es: newEditorState });
        updateLoaderState(false);
      }
    );
  };
  // Styles toogle ends here

  /**
   * On edit
   * @param {Object} x - The x
   * @param {string} type - The type of the edit
   * @returns {void}
   */
  const onEdit = (x, type) => {
    let texobj;
    if (x && x.v) {
      texobj = null;
      if (x.v === -42) {
        // new pricing block
        dispatch({ type: 'setPriceEdit', priceEdit: x });
      } else if (x.v === -43) {
        // new miles block
        dispatch({ type: 'setTimeEdit', timeEdit: x });
      } else if (type === 'gallery') {
        dispatch({ type: 'setGalleryEdit', galleryEdit: x });
      } else if (type === 'html') {
        dispatch({ type: 'setHtmlEdit', htmlEdit: x });
      } else if (Array.isArray(x.v)) {
        dispatch({ type: 'setTableEdit', tableEdit: x });
      } else {
        try {
          texobj = JSON.parse(x.v);
        } catch (exx) {
          // console.log(exx);
        }
        if (texobj && texobj.deliverables) {
          dispatch({ type: 'setPriceEdit', priceEdit: x });
        } else if (texobj && texobj.milestones) {
          dispatch({ type: 'setTimeEdit', timeEdit: x });
        } else if (texobj && texobj.superPowers) {
          dispatch({ type: 'setSuperEdit', superEdit: x });
        }
      }
    }
  };

  // Render custom font color
  /**
   * Custom style function
   * @param {Object} styles - The styles
   * @returns {Object} - The output
   */
  const customStyleFn = (styles) => {
    const output = {};
    const fontFamilies = Object.keys(state?.styleMap || {});
    const fontToBeLoad = [];

    // find font, color, background etc
    styles.forEach((value) => {
      const styleValue = value?.toString();

      // find fonts
      if (!fontLoadedVar()) {
        if (fontFamilies.includes(styleValue) && !fontLoaded.includes(styleValue)) {
          fontToBeLoad.push(styleValue);
          fontLoaded.push(styleValue);
        }
      }

      // find color
      if (styleValue?.startsWith('rgba')) {
        output.color = styleValue;
      }
      // find background color
      if (styleValue?.startsWith('bg-')) {
        output.backgroundColor = styleValue.substring(3);
      }
    });

    if (fontToBeLoad.length && !fontLoadedVar()) {
      const fontList = isRtl ? fontListHebrew : fontListEnglish;
      const prelist = fontList.filter((fontList) => fontToBeLoad.includes(fontList.family));

      loadProposalFont(prelist, prop?.language);
    }

    return output;
  };

  /**
   * Handle move block
   * @param {Object} newEditorState - The new editor state
   * @param {Object} block - The block
   * @param {Object} selection - The selection
   * @returns {void}
   */
  const handleMoveBlock = async ({ newEditorState, block, selection }) => {
    try {
      const es = await AtomicBlockUtils.moveAtomicBlock(newEditorState, block, selection);
      focusEditor();
      handleEditorChange({ es });
    } catch (error) {
      message.error('Block cannot be moved next to itself');
    }
  };

  /**
   * Handle backspace
   * @param {Object} editorState - The editor state
   * @param {Object} selection - The selection
   * @returns {void}
   */
  const handleBackspace = (editorState, selection) => {
    const contentState = editorState.getCurrentContent();

    // Remove the selected text
    const newContentState = Modifier.removeRange(
      contentState,
      selection,
      'backward' // Can be 'backward' or 'forward' depending on how you want the range to be handled
    );

    // Push the new content state with the removed text to the editor state
    const newEditorState = EditorState.push(editorState, newContentState, 'remove-range');

    // Update the editor state with the new state
    handleEditorChange({ es: newEditorState });
  };

  /**
   * Handle key binding
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleKeyBindingFn = (e) => {
    if (e.shiftKey && e.keyCode === 13) {
      return 'shift+enter';
    } else if (e.ctrlKey || e.metaKey) {
      if (e.keyCode === 75) {
        // ctrl + k = insert link

        e.preventDefault();
        _toggleInlineStyle({
          withoutText: state.inlineToolbar.show ? false : true,
          inlineStyle: 'LINK',
        });
        return 'handled';
      } else if (e.keyCode === 220) {
        // ctrl + \ = clear formatting
        _clearFormatting(null, true);
        return 'handled';
      } else if (e.keyCode === 88 && e.shiftKey) {
        // ctrl + shift + x = strike through
        _toggleInlineStyle({ inlineStyle: 'STRIKETHROUGH' });
        return 'handled';
      } else if (e.keyCode === 90 || e.keyCode === 89) {
        if (state?.undoRedoHistory?.current) {
          return 'handled';
        }

        if (e.keyCode === 90 && !e.shiftKey) {
          e.preventDefault();
          undoRedoRef.current = 'undo';
          handleEditorChange({ es: EditorState.undo(state.editorState) });
        } else {
          e.preventDefault();
          undoRedoRef.current = 'redo';
          handleEditorChange({ es: EditorState.redo(state.editorState) });
        }
        return 'handled';
      }
      // else if ((e.keyCode === 90 && e.shiftKey) || e.keyCode === 89) {
      //   e.preventDefault();
      //   dispatch({ type: 'setKeyType', keyType: e.shiftKey ? 'redo' : 'undo' });
      //   return 'handled';
      // } else if (e.keyCode === 90) {
      //   e.preventDefault();
      //   dispatch({ type: 'setKeyType', keyType: 'undo' });
      //   return 'handled';
      // }
      // else if (e.keyCode === 89) {
      //   e.preventDefault();
      //   dispatch({ type: 'setKeyType', keyType: 'redo' });
      //   return 'handled';
      // }
      else if (e.keyCode === 83) {
        e.preventDefault();
        handleEditorChange({ es: state.editorState });
        return 'handled';
      } else if (e.shiftKey) {
        if (e.keyCode === 188) {
          // ctrl + shift + , = reduce font size
          _toggleFontInlineStyle({ inlineStyle: 12, type: 'decrease-size', fontFamilies: [] });
          return 'handled';
        } else if (e.keyCode === 190) {
          // ctrl + shift + . = increase font size
          _toggleFontInlineStyle({ inlineStyle: 18, type: 'increase-size', fontFamilies: [] });
          return 'handled';
        } else if (e.keyCode === 69 || e.keyCode === 82 || e.keyCode === 76 || e.keyCode === 74) {
          // ctrl + shift + (e,j,l,r) = text alignment

          e.preventDefault();
          const align = {
            69: 'editor-align-center',
            74: 'editor-align-justify',
            76: 'editor-align-left',
            82: 'editor-align-right',
          };
          _toggleCustomBockStyle({ blockType: align[e.keyCode], type: 'align' });
          return 'handled';
        }
      }
    } else if (e.key === 'Tab' || e.keyCode === 9) {
      e.preventDefault();
      /*
      NOTE : dont allow user to enter multiple tab on list which will 
      make the bullets 1. and 1.0.1 or 1.0.0.1 (with 1 or 2 depth skip)
      */
      const selection = state.editorState.getSelection();
      const currentBlockKey = selection.getStartKey();
      const currentContent = state.editorState.getCurrentContent();

      const blockBefore = currentContent.getBlockBefore(currentBlockKey);
      const currentBlock = currentContent.getBlockForKey(currentBlockKey);

      /*
      if current block is list AND
      ( if previous block is not list OR
      current block depth > previous block depth dont allow tab )
      */
      if (
        currentBlock?.getType().endsWith('list-item') &&
        (!blockBefore.getType().endsWith('list-item') ||
          currentBlock?.depth > (blockBefore?.depth ?? 0))
      ) {
        return 'handled';
      }

      utils.onTab({
        e,
        editorRef,
        editorChange,
        editorState: state.editorState,
        props: { ...props, saveDraft },
        dispatch,
        onToggleCustomBockStyle: _toggleCustomBockStyle,
        tabType: e.shiftKey ? 'left-intent' : 'right-intent',
      });
      return 'handled';
    }

    // handle backscpae on selected text
    if (e.keyCode === 8) {
      const { editorState } = state;
      const selection = editorState.getSelection();
      if (!selection.isCollapsed()) {
        handleBackspace(editorState, selection);
        return 'handled'; // Indicate that the backspace command was handled
      }
    }

    return getDefaultKeyBinding(e);
  };

  /**
   * Handle editor drop
   * @param {Object} selection - The selection
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleEditorDrop = (selection, e) => {
    if (dropDisabled) {
      return 'handled';
    }

    const elementType = e.getText();
    let newEditorState = EditorState.acceptSelection(state.editorState, selection);
    let insertAfterBlock = false;

    if (prop.importedProposal && elementType === 'add-text') {
      const endKey = selection.getEndKey();

      const selectedElement = document.querySelector(`[data-offset-key="${endKey}-0-0"]`); // get element where the new element is dropped
      const clientRect = selectedElement?.getBoundingClientRect();

      let newSelection;
      if (clientRect?.top > 0) {
        // add new element to the before the current section
        const { editorState, newBlockKey } = utils.insertEmptyBlock('before', newEditorState);

        newEditorState = editorState;

        newSelection = new SelectionState({
          anchorKey: newBlockKey,
          anchorOffset: 0,
          focusKey: newBlockKey,
          focusOffset: 0,
          isBackward: true,
          hasFocus: true,
        });
      } else {
        // add element after the current section
        newSelection = new SelectionState({
          anchorKey: endKey,
          anchorOffset: 1,
          focusKey: endKey,
          focusOffset: 1,
        });
        insertAfterBlock = true;
      }
      newEditorState = EditorState.forceSelection(newEditorState, newSelection);
    }
    // Can be used in future to move elements between sections
    // const dropSectionName = e.data.getData('sectionName');

    // if (dropSectionName) {
    //   const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);
    //   let tempEditorState = EditorState.createWithContent(
    //     convertFromRaw(prop.draft[dropSectionName].raw, extendedBlockRenderMap),
    //     decorator
    //   );
    //   const newTempEditorContentState = tempEditorState.getCurrentContent();
    //   const newTempEditorBlockMap = newTempEditorContentState.blockMap.delete(elementType);
    //   const newTempEditorContentWithoutEmptyBlock = newTempEditorContentState.set(
    //     'blockMap',
    //     newTempEditorBlockMap
    //   );
    //   const editorStateWithoutBlock = EditorState.push(
    //     tempEditorState,
    //     newTempEditorContentWithoutEmptyBlock,
    //     'remove-block'
    //   );

    //   saveTempDraft(dropSectionName, 'raw')(editorStateWithoutBlock, () => {});
    //   const block = tempEditorState.getCurrentContent().getBlockForKey(elementType);
    //   handleMoveBlock({ newEditorState, block, selection });
    // }

    const block = newEditorState.getCurrentContent().getBlockForKey(elementType);

    if (!block) {
      switch (elementType) {
        case 'add-text':
          handleTextInsert(newEditorState, insertAfterBlock);
          return 'handled';
        case 'add-divider':
          handleInsertDivider(newEditorState);
          return 'handled';
        case 'add-blockquote':
          _toggleBlockType('blockquote', prop.importedProposal ? 'blockquote' : '', newEditorState);
          return 'handled';
        case 'add-image':
          handleImageInsert(newEditorState);
          return 'handled';
        case 'add-gallery':
          handleInsertGallery(newEditorState);
          return 'handled';
        case 'add-video':
          handleInsertVideo(newEditorState);
          return 'handled';
        case 'add-table':
          handleInsertTable(newEditorState);
          return 'handled';
        case 'add-testimonial':
          handleInsertTestimonial(newEditorState);
          return 'handled';
        case 'add-form':
          handleInsertForm(newEditorState);
          return 'handled';
        case 'add-price':
          handleInsertPriceMiles('price', newEditorState);
          return 'handled';
        case 'add-miles':
          handleInsertPriceMiles('miles', newEditorState);
          return 'handled';
        case 'add-html':
          handleInsertHTML(newEditorState);
          return 'handled';
        default:
          if (elementType?.startsWith('toolbar-library-media-')) {
            const src = e.data.getData('src');
            handleInsertFromMedia(src, newEditorState);
            return 'handled';
          }
          return false;
      }
    } else {
      handleMoveBlock({ newEditorState, block, selection });
      return 'handled';
    }
  };

  /**
   * Handle pasted text
   * @param {string} text - The text
   * @param {string} html - The HTML
   * @returns {void}
   */
  const handlePastedText = async (text, html) => {
    const selection = state.editorState.getSelection();
    const currentContent = state.editorState.getCurrentContent();

    if (html) {
      /* Todo: Fn:convertFromHTML allow as to parse entity and blocks. 
         using that we can convert our custom pricing and etc component. right now we converting it to text 
      */
      const blocks = convertFromHTML(html);

      const newContent = Modifier.replaceWithFragment(
        currentContent,
        selection,
        ContentState.createFromBlockArray(blocks, blocks.entityMap).getBlockMap()
      );

      const entityKeys = convertToRaw(newContent);

      if (entityKeys?.blocks?.length > 0) {
        const modifiedBlocks = entityKeys?.blocks?.map((block) => {
          if (block.type === 'atomic' && !block.entityRanges.length) {
            return {
              key: block.key,
              type: 'unstyled',
              text: block.text,
              depth: 0,
              inlineStyleRanges: [],
              entityRanges: [],
              data: {},
            };
          }
          return block;
        });

        const modifiedContent = convertFromRaw({
          entityMap: entityKeys.entityMap,
          blocks: modifiedBlocks,
        });

        const newSelection = new SelectionState({
          anchorKey: _.last(modifiedBlocks).key,
          anchorOffset: _.last(modifiedBlocks)?.text?.length || 0,
          focusKey: _.last(modifiedBlocks).key,
          focusOffset: _.last(modifiedBlocks)?.text?.length || 0,
          hasFocus: true,
        });

        const editorState = await EditorState.acceptSelection(
          EditorState.push(state.editorState, modifiedContent, 'insert-characters'),
          newSelection
        );
        handleEditorChange({
          es: editorState,
          loaderInActive: true,
        });
      }

      return true;
    }
    return false;

    /*
     * @todo: check
     *
    if (!selection.anchorOffset) {
      const newContent = Modifier.insertText(currentContent, selection, text);

      handleEditorChange({
        es: EditorState.push(state.editorState, newContent, 'insert-characters'),
      });
      return true;
    } else {
      return false;
    }
    */
  };

  /**
   * Handle insert variable
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleInsertVariable = (e) => {
    const text = `${e.keyPath[1]}.${e.keyPath[0]}}}`;
    const { editorState } = state;
    const selection = state.selectionState || editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const newContent = Modifier.insertText(contentState, selection, text);
    const withVariableText = EditorState.push(editorState, newContent, 'insert-characters');
    const withProperCursor = EditorState.forceSelection(
      withVariableText,
      newContent.getSelectionAfter()
    );
    handleEditorChange({ es: withProperCursor });

    dispatch({ type: 'setDisplayVariableInput', displayVariableInput: false });
    dispatch({ type: 'setEditorReadOnly', editorReadOnly: false });
  };

  /**
   * Handle global click
   * @param {Object} e - The event
   * @returns {void}
   */
  const handleGlobalClick = (e) => {
    if (state.displayColorPicker && state.editorReadOnly) {
      if (state.colorOpen) {
        // if updated in last 400ms then return no need to close
        // probably switched from COLOR to BACKGROUND
        if (state?.updated + 400 > Date.now()) {
          return;
        }

        if (e.target.id.indexOf('rc-editable-input') === -1) {
          closeColor();

          dispatch({
            type: 'setColorOpen',
            colorOpen: false,
          });
        }
      } else {
        dispatch({
          type: 'setColorOpen',
          colorOpen: true,
        });
      }
    }
    setActiveEditorId(id);
  };

  /**
   * Handle close variable menu
   * @returns {void}
   */
  const handleCloseVariableMenu = () => {
    dispatch({ type: 'setDisplayVariableInput', displayVariableInput: false });
  };

  useEffect(() => {
    if (activeEditorId !== id) {
      closeColor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEditorId, id]);

  /**
   * Handle rich editor blur
   * @param {Object} event - The event
   * @param {string} type - The type of the event
   * @returns {void}
   */
  const handleRichEditorBlur = (event, type) => {
    const relatedTarget =
      event.relatedTarget ||
      event.currentTarget ||
      event.explicitOriginalTarget ||
      document.activeElement;

    if (
      (!event?.target?.className?.match?.(/rich-editor/i) &&
        !event?.target?.className?.match?.(/rich-editor-toolbar-header/i) &&
        !event?.target?.className?.match?.(/DraftEditor-root/i) &&
        !event?.target?.className?.match?.(/DraftEditor-editorContainer/i) &&
        !event?.target?.className?.match?.(/paragraph/i) &&
        !event?.target?.className?.match?.(/DraftStyleDefault/i) &&
        !event?.target?.className?.match?.(/input/i) &&
        !event?.target?.className?.match?.(/simple-section/i) &&
        !event?.target?.className?.match?.(/ai-toolbar/i) &&
        !event?.target?.className?.match?.(/ant-dropdown/i) &&
        !event?.target?.className?.match?.(/hex-value-input/i) &&
        !event?.target?.closest(`.anticon`) &&
        !relatedTarget?.className?.match?.(/input/i) &&
        !state.displayColorPicker) ||
      !!(
        displayAIToolBar.current &&
        displayAIToolBar.current !== `${sectionName}_${simple}` &&
        !state.aISelectionState
      ) ||
      event?.target?.closest(`#r_${id}`)?.id !== `r_${id}` ||
      (event.target.className?.match?.(/ai-toolbar-input-text-area/i) && displayAIToolBar.current)
    ) {
      const contentIsntImg = content !== '<img />'; // Do not attempt AI open for img content
      let rawNew = convertToRaw(state?.editorState?.getCurrentContent()); // fixes reverting issue causes undo selection after AI open
      if (rawNew) {
        let newEditorState = EditorState.createWithContent(
          convertFromRaw(rawNew, extendedBlockRenderMap),
          decorator
        );
        displayAIToolBar.current = '';
        displayAIToolBarCursor.current = '';
        setAskAI(false);
        dispatch({ type: 'setAISelectionState', aISelectionState: '' });

        if (!isCursorAtEndOfBlock() && contentIsntImg) {
          newEditorState = _clearFormatting(newEditorState, false);
        }
        if (contentIsntImg) {
          const emptySelection = SelectionState.createEmpty().set('hasFocus', false);
          newEditorState = EditorState.forceSelection(newEditorState, emptySelection);
        }
        editorRef.current = newEditorState;
        dispatch({ type: 'setEditorState', editorState: newEditorState });
        dispatch({
          type: 'setInlineToolbar',
          inlineToolbar: {
            show: false,
          },
        });
      } else if (content) {
        let newEditorState = EditorState.createWithContent(
          stateFromHTML(content, htmlOptions || {}),
          decorator
        );
        displayAIToolBar.current = '';
        displayAIToolBarCursor.current = '';
        setAskAI(false);
        dispatch({ type: 'setAISelectionState', aISelectionState: '' });
        if (!isCursorAtEndOfBlock()) {
          newEditorState = _clearFormatting(newEditorState, false);
        }
        dispatch({ type: 'setEditorState', editorState: newEditorState });
        editorRef.current = newEditorState;
      }
    }
  };

  /**
   * Handle mouse down - This is for handling multiple selection inside a section
   * @param {Object} event - The event
   * @returns {void}
   */
  const handleMouseDown = (event) => {
    if (
      ((!user?.profile?.enableAITool && prop?.enableAITool) ||
        (user?.profile?.enableAITool && prop?.enableAITool)) &&
      editorRef.current &&
      sectionName !== 'header' &&
      sectionName !== 'signature'
    ) {
      if (
        event.target.className === 'custom-select-selector' ||
        event.target.className === 'custom-select-item-content' ||
        event.target.className === 'hex-value-input' ||
        event.target.className === 'tool-button' ||
        event.target.className?.baseVal === 'toolbarSvgClear' ||
        event.target.className?.baseVal === 'ask-ai-icon-container' ||
        event.target.className?.baseVal === 'ask-ai-icon' ||
        event.target.className?.match?.(/ai-toolbar-bot/) ||
        event.target.className?.match?.(/rich-editor-toolbar-clear/) ||
        event.target.className?.match?.(/ask-ai-icon/) ||
        event.target.className?.match?.(/ant-dropdown-trigger/) ||
        event.target.className?.match?.(/ant-col/) ||
        event.target.className?.match?.(/ant-dropdown-menu/) ||
        event.target.closest('.ai-toolbar', 'class')
      ) {
        return;
      }

      const currentSelection = editorRef?.current?.getSelection();
      const previousSelection = state?.aISelectionState;

      if (event?.detail >= 3) {
        const selection = editorRef?.current?.getSelection();
        const anchorKey = selection?.getAnchorKey();
        const currentContent = editorRef?.current?.getCurrentContent();
        const currentBlock = currentContent?.getBlockForKey(anchorKey);

        if (!currentBlock) {
          return;
        }

        const contentState = ContentState.createFromBlockArray([currentBlock]);
        const ent = currentBlock?.getEntityAt(0);

        if (ent) {
          const entity = contentState.getEntity(ent);
          const type = entity?.type;
          const data = entity?.data;

          if (
            data &&
            data.data &&
            (type === 'table' || type === 'testimonial' || type === 'form')
          ) {
            return;
          }
        }
        const selected = getFragmentFromSelection(editorRef?.current);

        let selectedText = selected ? selected.map((x) => x.getText()).join('\n') : '';

        const newSelection = new SelectionState({
          anchorKey: currentSelection.getAnchorKey(),
          anchorOffset:
            currentSelection.getAnchorOffset() === currentSelection.getFocusOffset()
              ? 0
              : currentSelection.getAnchorOffset(),
          focusKey: currentSelection.getFocusKey(),
          focusOffset: currentSelection.getFocusOffset() || selectedText?.length - 1, //currentSelection.getAnchorOffset(),
          hasFocus: true,
        });
        // commented in favor of showing AI popup at bottom of selected text
        let newEditorState = EditorState.acceptSelection(editorRef?.current, newSelection);
        dispatch({ type: 'setEditorState', editorState: newEditorState });
        dispatch({ type: 'setAISelectionState', aISelectionState: newSelection });

        // Always apply background color for triple-click selection
        newEditorState = formatBackgroundSelectionColor(newEditorState, true);

        if (!displayAIToolBar.current) {
          console.log('not showing a tool bar');
          displayAIToolBar.current = `${sectionName}_${simple}`;
        }
        editorRef.current = newEditorState;
        return;
      }

      if (currentSelection && previousSelection) {
        const currentSelectionAnchorOffset = currentSelection?.getAnchorOffset();
        const previousSelectionAnchorOffset = previousSelection?.getAnchorOffset();
        if (previousSelectionAnchorOffset !== currentSelectionAnchorOffset) {
          handleClickOutside(null, 'inside', state?.aISelectionState);
        }
      }
    }
  };

  /**
   * Render rich editor
   * @returns {void}
   */
  if (!editorRef.current) {
    return null;
  }
  const path = window.location.pathname;
  const isActive = state?.editorState?.getSelection().getHasFocus() || state.lineHeightInput;
  const uniqueKey = `${id}-${columnIndex}`;
  const canUseAI =
    (displayAIToolBar.current || displayAIToolBarCursor.current) &&
    ((!user?.profile?.enableAITool && prop?.enableAITool) ||
      (user?.profile?.enableAITool && prop?.enableAITool) ||
      wixEditor ||
      templateWixEditor) &&
    !state.displayLink &&
    !state.toolOpen &&
    sectionName !== 'header' &&
    sectionName !== 'signature' &&
    !state.displayVariableInput &&
    activeEditorId === id &&
    !/Mobi|Android|Tablet/i.test(navigator.userAgent) &&
    !commonHelper.isIOS() &&
    !commonHelper.isTablet();
  const aiStyle = _.extend({}, state.inlineToolbar.position);

  /**
   * Handle select from library
   * @returns {void}
   */
  const handleSelectFromLibrary = () => {
    dispatch({ type: 'setShowImageSelectionModal', imageSelectionModal: true });
    dispatch({ type: 'setImageSelectionModalType', imageSelectionModalType: 'library' });
  };

  /**
   * Show AI image prompt modal
   * @returns {void}
   */
  const showAIImagePromptModal = () => {
    dispatch({ type: 'setShowImageSelectionModal', imageSelectionModal: true });
    dispatch({ type: 'setImageSelectionModalType', imageSelectionModalType: 'ai' });
  };

  /**
   * Show image prompt modal
   * @param {boolean} show - Whether to show the image prompt modal
   * @returns {void}
   */
  const showAIImageModal = (show) => {
    dispatch({ type: 'setShowImageSelectionModal', imageSelectionModal: show });
    if (show) {
      dispatch({ type: 'setRegenerateMode', isRegenerateMode: true });
      dispatch({ type: 'setImageSelectionModalType', imageSelectionModalType: 'ai' });
    }
  };

  /**
   * Show image selection modal
   * @param {boolean} show - Whether to show the image selection modal
   * @param {string} type - The type of the image selection modal
   * @returns {void}
   */
  const showImageSelectionModal = (show, type) => {
    dispatch({ type: 'setShowImageSelectionModal', imageSelectionModal: show });
    if (show) {
      dispatch({ type: 'setImageSelectionModalType', imageSelectionModalType: type || 'ai' });
    }
  };

  /**
   * Set current image block
   * @param {string} blockKey - The block key
   * @returns {void}
   */
  const setCurrentImageBlock = (blockKey) => {
    dispatch({ type: 'setCurrentImageBlock', currentImageBlock: blockKey });
  };

  return (
    <div
      className={`rich-editor ${activeEditorId === id ? 'active' : 'inactive'}`}
      ref={richEditorRef}
      id={`r_${id}`}
      onClick={handleGlobalClick}
      onBlur={handleRichEditorBlur}
      onMouseUp={handleMouseDown}
      tabIndex="-1">
      <HyperLinkModals
        dispatch={dispatch}
        state={state}
        editor={editor}
        handleEditorChange={handleEditorChange}
        setShouldCheckUrl={setShouldCheckUrl}
        promptForLink={promptForLink}
        numberOfColumns={numberOfColumns}
        columnIndex={columnIndex}
        isValidUrl={isValidUrl}
        setIsValidUrl={setIsValidUrl}
      />
      {state.displayHyperlinkInput && (
        <HyperLinkSection
          dispatch={dispatch}
          {...state.displayHyperlinkInput}
          columnIndex={columnIndex}
          numberOfColumns={numberOfColumns}
          isRtl={isRtl}
          uniqueKey={uniqueKey}
        />
      )}

      {state.displayVariableInput && (
        <RenderVariableInput
          state={state}
          prop={prop}
          handleInsertVariable={handleInsertVariable}
          active={isActive && activeEditorId === props.id}
          toolbarPosition={toolbarPosition}
          setToolbarPosition={setToolbarPosition}
          handleCloseVariableMenu={handleCloseVariableMenu}
        />
      )}
      {canUseAI && (
        <AIToolBar
          id={id}
          style={aiStyle}
          askAI={askAI}
          editorState={editorRef.current}
          aiLengthMenu={config?.aiPrompt?.lengthListOptions}
          dispatch={dispatch}
          handleEditorChange={handleEditorChange}
          clearFormatting={_clearFormatting}
          language={prop?.language?.toLowerCase() || 'english'}
          setAskAI={setAskAI}
          displayAIToolBar={displayAIToolBar}
          displayAIToolBarCursor={displayAIToolBarCursor}
          editorRef={editorRef}
          formatBackgroundSelectionColor={formatBackgroundSelectionColor}
          aiSaving={aiSaving}
          zoomValue={zoomValue}
          undoRedoHistory={state.undoRedoHistory}
          columnIndex={columnIndex}
          numberOfColumns={numberOfColumns}
          typeOfColumns={typeOfColumns}
          isRtl={isRtl}
        />
      )}

      <RichEditorModals
        prop={prop}
        user={user}
        config={config}
        state={state}
        dispatch={dispatch}
        handleEditorChange={handleEditorChange}
        saveProposal={saveProposal}
        saveUser={saveUser}
        setOpenVariablesList={setOpenVariablesList}
        focusEditor={focusEditor}
        checkSuspiciousUrl={checkSuspiciousUrl}
        configText={configText}
        // loadingAIImage={loadingAIImage}
        // handleAIImageSelection={handleAIImageSelection}
        // handleDropImage={handleDropImage}
        handleImageSelectUrl={handleImageSelectUrl}
      />

      {activeEditorId === id &&
      (['header', 'signature'].includes(sectionName) ||
        (!['header', 'signature'].includes(sectionName) && selectedBlock)) ? (
        <ToolBar
          // selectionActive={!state?.editorState?.getSelection().getHasFocus()}
          selectionActive={!state?.editorState?.getSelection().isCollapsed()}
          bodyFont={prop?.draft?.bodyFont || prop?.bodyFont}
          language={prop?.language?.toLowerCase() || 'english'}
          selectedBlock={selectedBlock}
          editorState={state.editorState}
          dispatch={dispatch}
          onToggle={_toggleInlineStyle}
          onToggleBlockType={_toggleBlockType}
          onToggleFontInlineStyle={_toggleFontInlineStyle}
          onToggleCustomBockStyle={_toggleCustomBockStyle}
          onClearFormatting={_clearFormatting}
          onSplitSection={_onSplitSection}
          zoomValue={zoomValue}
          user={user}
          displayColorPicker={state.displayColorPicker}
          colorPicker={
            <RenderColorPicker
              state={state}
              onToggle={_toggleInlineStyle}
              onClose={closeColor}
              className="inside-toolbar"
            />
          }
          isSection={featureFlag?.DISABLE_SPLIT_FOR_SECTIONS ? path.includes('/s/') : false}
          hideTools={hideTools || []}
          fallbackStyles={fallbackStyles}
        />
      ) : null}

      {contentType === 'image' && !raw ? (
        <div className="image-drop-section">
          <div className="custom-dropzone" onDrop={onImageDrop} onDragOver={handleImageDragOver}>
            <input type="file" accept="image/*" onChange={handleImageChange} id="image-upload" />
            <div className="dropzone-inner">
              <UploadCloudIcon className="upload-icon" />
              <label htmlFor="image-upload">
                Drag & drop your file or <span>Upload</span> from your computer
              </label>
              <div className="or-divider">
                <span>Or</span>
              </div>
              <div className="button-wrapper">
                <Button className="large-btn" block onClick={handleSelectFromLibrary}>
                  Choose from Library
                </Button>
                <Button className="large-btn" block onClick={showAIImagePromptModal}>
                  Create with AI
                </Button>
              </div>
            </div>
          </div>
          {showImageProgress && (
            <div className="logo-upload-progress">
              <p className="progress-title">Uploading Image...</p>
              <p className="upload-image-name" title={imageName}>
                {imageName}
              </p>
              <div className="spacer" />
              <Slider value={uploadStatus.percent} />
            </div>
          )}
        </div>
      ) : (
        <div
          {...(state.editorReadOnly ? {} : { onClick: focusEditor })}
          id={`draft-editor-${id}`}
          // onBlur={handleRichEditorBlur}
          // onMouseDown={handleMouseDown}
          // NOTE : on adding onDragOver, effectAllowed = "none" wont work
          // onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => {
            e.preventDefault();
            dragStartRef.current = true;
            if (dropDisabled) {
              e.dataTransfer.effectAllowed = 'none';
              if (!readOnly) {
                // make readonly
                setReadOnly(true);
              }
            }
          }}>
          <Editor
            ref={editor}
            readOnly={dropDisabled ? readOnly : state.editorReadOnly}
            spellCheck
            customStyleMap={state.styleMap}
            customStyleFn={customStyleFn}
            blockRenderMap={state.blockRenderMap}
            handleDrop={handleEditorDrop}
            // handleBeforeInput={handleBeforeInput}
            blockRendererFn={(block) =>
              blockRenderer({
                setElementsInSection,
                sectionType: contentType,
                numberOfColumns,
                setDraggingElement,
                sectionSpan,
                sectionName,
                block,
                state,
                wixEditor,
                templateWixEditor,
                dispatch,
                setDeletedBlockKeys,
                props: { ...props, saveDraft },
                editorRef,
                editorChange,
                upload,
                updateLoaderState,
                isEditingModal,
                uniqueKey,
                onEdit: (x, type) =>
                  onEdit(
                    {
                      v: x.v,
                      ...(x?.config && { config: x.config }),
                      save: (nv, saveSuccess) => {
                        x.save(nv);
                        setTimeout(() => saveDraft(x.editorState, saveSuccess), 300);
                      },
                      cancel: x.cancel,
                    },
                    type
                  ),
                handleImageInsert,
                handleInsertVideo,
                focusEditor,
                handleDropImage,
                showAIImageModal,
                showImageSelectionModal,
                setCurrentImageBlock,
                displayAIToolBarCursor,
              })
            }
            blockStyleFn={blockStyler}
            editorState={editorRef.current}
            placeholder={placeholder || configText('write here', true)}
            handleKeyCommand={(command) =>
              utils.handleKeyCommand({
                command,
                editorState: state.editorState,
                editorRef,
                editorChange,
                props: { ...props, saveDraft },
                dispatch,
                createCellBlock,
                updateSelection: utils.updateSelection,
              })
            }
            keyBindingFn={handleKeyBindingFn}
            onChange={(editorState) => {
              const currentContentState = editorRef.current.getCurrentContent();
              const newContentState = editorState.getCurrentContent();

              if (
                (!user?.profile?.enableAITool && prop?.enableAITool) ||
                (user?.profile?.enableAITool && prop?.enableAITool)
              ) {
                const currentSelection = editorRef?.current?.getSelection();
                const previousSelection = editorState?.getSelection();
                const currentSelectionOffset = currentSelection?.getAnchorOffset();
                const previousSelectionOffset = previousSelection?.getAnchorOffset();
                const currentSelectionFocusOffset = currentSelection?.getFocusOffset();
                const previousSelectionFocusOffset = previousSelection?.getFocusOffset();

                const oldSelected = getFragmentFromSelection(editorRef.current);
                const newSelected = getFragmentFromSelection(editorState);

                const newSelectedText = newSelected
                  ? newSelected.map((x) => x.getText()).join('\n')
                  : '';
                const oldSelectedText = oldSelected
                  ? oldSelected.map((x) => x.getText()).join('\n')
                  : '';

                const { language: proposalLangugae } = props?.prop;

                if (
                  state.aISelectionState &&
                  displayAIToolBar.current &&
                  currentContentState.getPlainText() === newContentState.getPlainText()
                ) {
                  updateSelectionInlineToolbar(props.id, proposalLangugae, dispatch);
                }

                if (
                  ((state.selectionState && displayAIToolBar.current) ||
                    (state.aISelectionState && displayAIToolBar.current)) &&
                  !(
                    previousSelectionOffset === currentSelectionOffset &&
                    currentSelectionFocusOffset !== previousSelectionFocusOffset
                  )
                ) {
                  if (
                    previousSelectionOffset === currentSelectionOffset &&
                    currentSelectionFocusOffset === previousSelectionFocusOffset
                  ) {
                    return;
                  } else if (newSelectedText && oldSelectedText !== newSelectedText) {
                    const selectionNew = editorState?.getSelection();
                    dispatch({ type: 'setAISelectionState', aISelectionState: selectionNew });
                    formatBackgroundSelectionColor(editorState);
                    return; // handleClickOutside(editorState);
                  } else if (!oldSelectedText && oldSelectedText !== newSelectedText) {
                    // handles drag, select and delete
                    return;
                  }
                }

                if (previousSelection?.getHasFocus()) {
                  const windowsSelection = window.getSelection();
                  const oldEditorStateSelection = state.editorState.getSelection();
                  let emptySelection;

                  if (
                    oldEditorStateSelection.getAnchorOffset() !==
                    oldEditorStateSelection.getFocusOffset()
                  ) {
                    emptySelection = new SelectionState({
                      anchorKey: previousSelection.getAnchorKey(),
                      anchorOffset: previousSelection.getAnchorOffset(),
                      focusKey: previousSelection.getFocusKey(),
                      focusOffset: previousSelection.getFocusOffset(),
                      hasFocus: true,
                    });
                  } else {
                    const previousSelectionAnchorOffset = previousSelection.getAnchorOffset();
                    const previousSelectionFocusOffset = previousSelection.getFocusOffset();
                    const previousSelectionAnchorKey = previousSelection.getAnchorKey();
                    const previousSelectionFocusKey = previousSelection.getFocusKey();

                    emptySelection = new SelectionState({
                      anchorKey: previousSelection.getAnchorKey(),
                      anchorOffset:
                        previousSelectionAnchorKey !== previousSelectionFocusKey
                          ? previousSelectionAnchorOffset
                          : previousSelectionAnchorOffset > previousSelectionFocusOffset
                          ? askAI
                            ? windowsSelection.focusOffset
                            : previousSelectionFocusOffset
                          : askAI
                          ? windowsSelection.anchorOffset
                          : previousSelectionAnchorOffset,
                      focusKey: previousSelection.getFocusKey(),
                      focusOffset:
                        previousSelectionAnchorKey !== previousSelectionFocusKey
                          ? previousSelectionFocusOffset
                          : previousSelectionAnchorOffset > previousSelectionFocusOffset
                          ? askAI
                            ? windowsSelection.anchorOffset
                            : previousSelectionAnchorOffset
                          : askAI
                          ? windowsSelection.focusOffset
                          : previousSelectionFocusOffset,
                      hasFocus: true,
                    });

                    editorState = EditorState.forceSelection(editorState, emptySelection);
                    editorRef.current = editorState;
                  }
                }
              }

              return editorChange({
                editorState,
                props: { ...props, saveDraft },
                editorRef,
                dispatch,
                updateSelection: utils.updateSelection,
                promptForVariable,
                setOpenVariablesList,
                setScrollingTo,
                setActiveEditorId,
                focusChanged: currentContentState === newContentState,
              });
            }}
            handlePastedText={handlePastedText}
          />
        </div>
      )}

      {floatingMenu?.id === id && (
        <FloatingMenu
          top={floatingMenu.top}
          handleToolbarElements={handleToolbarElements}
          isWix={wixEditor || templateWixEditor}
        />
      )}

      <input
        type="file"
        ref={imageInputRef}
        style={{ display: 'none' }}
        onChange={insertImage}
        accept="image/*"
      />

      {copyStatus && <Spin className="rich-copy-spinner" size="large" />}
    </div>
  );
};

RichEditor.defaultProps = {
  id: '',
  prop: '',
  raw: '',
  config: '',
  element: '',
  blockRenderMap: null,
  content: '',
  htmlOptions: '',
  sectionName: '',
  simple: '',
  placeholder: '',
  contentType: '',
  setElement: () => {},
  saveProposal: () => {},
  saveUser: () => {},
  zoomValue: 100,
  updateLoaderState: () => {},
  saveProposalDraft: () => {},
  setSavingDraftProposal: () => {},
  columnIndex: 0,
  numberOfColumns: 1,
  wixEditor: false,
  templateWixEditor: false,
  askAI: false,
  setAskAI: () => {},
  setElementsInSection: () => {},
};

RichEditor.propTypes = {
  id: PropTypes.string,
  element: PropTypes.string,
  prop: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.instanceOf(null)]),
  raw: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  config: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  blockRenderMap: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  content: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  htmlOptions: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  sectionName: PropTypes.string,
  simple: PropTypes.number,
  placeholder: PropTypes.string,
  linkTextValue: PropTypes.string,
  contentType: PropTypes.string,
  setElement: PropTypes.func,
  saveProposal: PropTypes.func,
  zoomValue: PropTypes.number,
  saveUser: PropTypes.func,
  updateLoaderState: PropTypes.func,
  saveProposalDraft: PropTypes.func,
  setSavingDraftProposal: PropTypes.func,
  setDisablePreview: PropTypes.func.isRequired,
  numberOfColumns: PropTypes.number,
  sectionSpan: PropTypes.number,
  titleSpan: PropTypes.number,
  setElementsInSection: PropTypes.func,
  wixEditor: PropTypes.bool,
  templateWixEditor: PropTypes.bool,
  askAI: PropTypes.bool,
  setAskAI: PropTypes.func,
  typeOfColumns: PropTypes.string,
};

export default RichEditor;

const RenderVariableInput = ({
  state,
  prop,
  handleInsertVariable,
  active,
  toolbarPosition,
  setToolbarPosition,
  handleCloseVariableMenu,
}) => {
  let _position = state.inlineToolbar?.position || {};

  let position = {
    left: _position?.left || 0,
    right: _position?.right || 0,
    bottom: _position?.bottom || 0,
    top: _position?.top || 0,
  };

  // if left/right > 0, it means correct position, so save it for future use
  if ((position?.left > 0 || position?.right > 0) && !_.isEqual(position, toolbarPosition)) {
    setToolbarPosition({ ...position });
  }

  if (position.left < 1 || position.right < 1) {
    // if left/right < 0, it means wrong position, so use position from cache
    position = { ...toolbarPosition };
  }
  const variables = prop?.draft?.variables || ELEMENTS_VARIABLES();

  return (
    <Menu
      className={`variables-menu ${prop.language || 'english'}`}
      style={{
        position: 'absolute',
        zIndex: '10',
        width: '10em',
        color: '#05034D',
        boxShadow: '0px 5px 20px rgba(4, 3, 49, 0.15)',
        borderRadius: '4px !important',
        display: active ? 'block' : 'none',
        height: 'fit-content',
        ...position,
      }}
      onMouseLeave={handleCloseVariableMenu}
      onClick={(e) => handleInsertVariable(e)}
      expandIcon={<ChevronIcon className={`variables-menu-chevron-icon ${prop.language}`} />}>
      {Object.keys(variables).map((value) => {
        return (
          <SubMenu
            popupClassName={`variables-submenu-items ${prop.language}`}
            className={`variables-menu-items ${prop.language}`}
            key={value}
            title={value}>
            {Object.keys(variables[value]).map((variable) => {
              return (
                <Menu.Item className={`variables-menu-items ${prop.language}`} key={variable}>
                  {variable}
                </Menu.Item>
              );
            })}
          </SubMenu>
        );
      })}
    </Menu>
  );
};
